import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Typography,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PrintIcon from "@mui/icons-material/Print";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import InventoryIcon from "@mui/icons-material/Inventory2";
import TuneIcon from "@mui/icons-material/Tune";
import { useParams } from "react-router-dom";
import useWorkflowDisplaySettings from "../../../../hooks/useWorkflowDisplaySettings";
import axios from "axios";
import InstanceLeftSidebar from "../../InstanceLeftSidebar";
import WarehouseBinPickerMap from "../../../../components/Warehouse/WarehouseBinPickerMap";
import {
  fetchBinReceipts,
  fetchReceivedHistory,
  fetchPoReceiptEvents,
  fetchPoReceipts,
  fetchWarehouseLayout,
  fetchWarehouses,
  savePoReceipt,
} from "../../../../services/shipping";
import {
  buildReceivedHistoryDisplayRows,
  countReceiptHistoryTrackedBoxes,
  filterReceivingQueueRows,
} from "./receivingHistoryRows";
import { resolvePurchaseOrderShippingMethodLabel } from "../../../../utils/generatedPoShippingDisplay";

const DEFAULT_RECEIVING_ELIGIBLE_PO_STATUSES = [
  "Shipped",
  "Partially Received",
];
const HISTORY_ALL_WAREHOUSES_KEY = "__all__";

const splitTrackingTokens = (value) =>
  String(value || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

const normalizeTrackingRef = (value) => String(value || "").trim().toLowerCase();

const uniqueTrackingRefs = (values = []) => {
  const seen = new Set();
  const unique = [];
  values.forEach((value) => {
    const raw = String(value || "").trim();
    if (!raw) return;
    const key = normalizeTrackingRef(raw);
    if (!key || seen.has(key)) return;
    seen.add(key);
    unique.push(raw);
  });
  return unique;
};

const parseTrackingList = (trackingRaw) => {
  if (!trackingRaw) return [];
  const rawList = Array.isArray(trackingRaw) ? trackingRaw : [trackingRaw];
  return uniqueTrackingRefs(rawList.flatMap((item) => splitTrackingTokens(item)));
};

const parseTrackingCount = (trackingRaw) => parseTrackingList(trackingRaw).length;

const parseSupplierInfo = (supplierInfo) => {
  if (!supplierInfo) return {};
  if (typeof supplierInfo === "object") return supplierInfo;
  if (typeof supplierInfo === "string") {
    try {
      return JSON.parse(supplierInfo);
    } catch {
      return {};
    }
  }
  return {};
};

const normalizeNoteMessage = (note = {}) =>
  String(note?.messageText || note?.message || note?.text || note?.note || note?.comment || note?.messageHtml || "").trim();
const noteActorName = (note = {}) =>
  String(note?.userName || note?.by || `${note?.user?.firstName || ""} ${note?.user?.lastName || ""}`.trim() || note?.user?.name || note?.user?.email || note?.email || "Unknown").trim();
const renderMentionsAsHtml = (text) =>
  String(text || "").replace(
    /@\[(?<display>[^\]]+)\]\((?<id>[^)]+)\)/g,
    (_m, _d, _id, _off, _str, groups) => `<b>@${(groups?.display || "").replace(/[<>&]/g, "")}</b>`
  );

const isDecorationPurchaseOrder = (row = {}) => {
  const info = parseSupplierInfo(row?.supplierInfo);
  const typeValues = [row?.purchaseOrderType, info?.type].map((value) => String(value || "").trim());
  if (typeValues.some((type) => ["Different_supplier_decoration", "Same_supplier_decoration", "decoration"].includes(type))) {
    return true;
  }
  if (info?.decoratorId != null && String(info.decoratorId).trim()) return true;
  if (Array.isArray(info?.charges) && info.charges.some((charge) => charge?.decoratorId != null)) return true;
  return false;
};

const normalizeImageUrl = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith("//")) return `https:${raw}`;
  return raw;
};

const resolvePoGoodsImage = (poRow) => {
  const candidates = Array.isArray(poRow?.items) ? poRow.items : [];
  for (const item of candidates) {
    const direct = [
      item?.imageUrl,
      item?.ImageUrl,
      item?.productImage,
      item?.thumbnail,
      item?.photoUrl,
      item?.previewImage,
    ].map(normalizeImageUrl).find(Boolean);
    if (direct) return direct;

    const imageArray = Array.isArray(item?.images) ? item.images : [];
    for (const imageEntry of imageArray) {
      if (typeof imageEntry === "string") {
        const parsed = normalizeImageUrl(imageEntry);
        if (parsed) return parsed;
      }
      const nested = [
        imageEntry?.url,
        imageEntry?.secure_url,
        imageEntry?.imageUrl,
        imageEntry?.ImageUrl,
        imageEntry?.thumbnail,
      ].map(normalizeImageUrl).find(Boolean);
      if (nested) return nested;
    }
  }
  return "";
};

const extractWarehouseLayout = (res) =>
  res?.layout ||
  res?.warehouseLayout ||
  res?.warehouse?.layout ||
  res?.data?.layout ||
  null;

const parseWarehouseBins = (layout) => {
  if (!layout || !Array.isArray(layout?.levels)) return [];
  const bins = [];
  layout.levels.forEach((level) => {
    Object.entries(level?.cells || {}).forEach(([cellKey, cell]) => {
      if (cell?.type !== "bin") return;
      bins.push({
        key: `${level.id}:${cellKey}`,
        warehouseLevel: level.id,
        warehouseCell: cellKey,
        label: cell?.label || `Bin ${cellKey}`,
        levelName: level?.name || level?.id,
      });
    });
  });
  return bins;
};

const normalizeLineKey = (value) => String(value ?? "").trim();
const formatEventTimestamp = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const formatReceivedQuantitySummary = (history = {}) => {
  if (history?.totalReceivedQuantity == null) return "—";
  const received = Number(history?.totalReceivedQuantity || 0);
  const expected = Number(history?.totalExpectedQuantity);
  if (Number.isFinite(expected) && expected > 0) {
    return `${received} / ${expected}`;
  }
  return `${received}`;
};

const buildCanonicalPoLineKey = ({
  pricing,
  item,
  itemIndex,
  pricingIndex,
}) => {
  const pricingId = normalizeLineKey(pricing?.id || pricing?._id || pricing?.pricingId);
  if (pricingId) return pricingId;

  const size = normalizeLineKey(pricing?.Size ?? pricing?.size);
  const color = normalizeLineKey(pricing?.Color ?? pricing?.color);
  const sku = normalizeLineKey(pricing?.SKU ?? pricing?.sku ?? item?.SKU ?? item?.sku);
  const orderItemId = normalizeLineKey(item?.id || item?._id || item?.poItemId);
  const productId = normalizeLineKey(item?.ProductId || item?.productId);
  const discriminator = [sku || "sku", size || "na", color || "na"].join(":");

  if (orderItemId) return `oi:${orderItemId}:${discriminator}`;
  if (productId) return `pid:${productId}:${discriminator}`;
  return normalizeLineKey(`item-${itemIndex + 1}:pricing-${pricingIndex + 1}:${discriminator}`);
};

const isReceivableWarehouseLine = (row = {}) => {
  const type = String(row?.Type || row?.type || "").trim().toLowerCase();
  const subType = String(row?.SubType || row?.subType || "").trim().toLowerCase();

  if (type === "cost") return false;
  if (subType === "inbound freight") return false;

  return true;
};

const getItemPricingRows = (item = {}) => {
  const lower = Array.isArray(item?.pricings) ? item.pricings : [];
  const upper = Array.isArray(item?.Pricing) ? item.Pricing : [];
  return [...lower, ...upper];
};

const getApprovedLineItems = (poRow) => {
  const supplierInfo = parseSupplierInfo(poRow?.supplierInfo);
  const approvedLines = Array.isArray(supplierInfo?.approvedLines) ? supplierInfo.approvedLines : [];

  return approvedLines
    .map((approvedLine) => approvedLine?.line)
    .filter((line) => line && typeof line === "object");
};

const parsePoLineItems = (poRow) => {
  const items = Array.isArray(poRow?.items) ? poRow.items : [];
  const approvedLineItems = getApprovedLineItems(poRow);
  const approvedProductIds = new Set(
    approvedLineItems
      .map((item) => normalizeLineKey(item?.ProductId || item?.productId || item?.id))
      .filter(Boolean)
  );
  const lines = [];
  const seenLineKeys = new Set();

  [...approvedLineItems, ...items].forEach((itemRef, itemIndex) => {
    const productId = normalizeLineKey(itemRef?.ProductId || itemRef?.productId || itemRef?.id || itemRef);
    const hasApprovedBreakdownForProduct =
      approvedProductIds.has(productId) &&
      !approvedLineItems.includes(itemRef);
    if (hasApprovedBreakdownForProduct) return;

    const item = itemRef && typeof itemRef === "object" ? itemRef : null;
    if (!item) return;

    const itemId = normalizeLineKey(item?.id || item?._id || item?.poItemId || item?.ProductId || item?.productId || `item-${itemIndex + 1}`);
    const itemLabelBase = String(
      item?.productName
      || item?.name
      || item?.itemName
      || item?.description
      || item?.sku
      || `PO Line ${itemIndex + 1}`
    ).trim();
    const itemSku = String(item?.SKU || item?.sku || "").trim();
    const itemImage = normalizeImageUrl(
      item?.imageUrl
      || item?.ImageUrl
      || item?.productImage
      || item?.thumbnail
      || item?.photoUrl
      || item?.previewImage
    );

    // PricingTable renders PO product lines from `pricings`; use those as receivable rows.
    const pricingRows = getItemPricingRows(item);
    pricingRows.forEach((pricing, pricingIndex) => {
      if (!isReceivableWarehouseLine(pricing)) return;
      const qty = Number(pricing?.Quantity ?? pricing?.quantity);
      if (!Number.isFinite(qty) || qty <= 0) return;

      const size = String(pricing?.Size ?? pricing?.size ?? "").trim();
      const color = String(pricing?.Color ?? pricing?.color ?? "").trim();
      const variantLabel = `${size}${size && color ? " - " : ""}${color}`.trim();
      const rowSku = String(pricing?.SKU ?? pricing?.sku ?? itemSku).trim();
      const lineKey = buildCanonicalPoLineKey({ pricing, item, itemIndex, pricingIndex });
      const normalizedLineKey = normalizeLineKey(lineKey);
      if (!normalizedLineKey || seenLineKeys.has(normalizedLineKey)) return;
      seenLineKeys.add(normalizedLineKey);

      lines.push({
        lineKey: normalizedLineKey,
        lineLabel: variantLabel || itemLabelBase || `PO Line ${itemIndex + 1}`,
        orderedQuantity: qty,
        unit: String(pricing?.unit || pricing?.uom || item?.unit || item?.uom || "").trim() || null,
        description: String(item?.description || item?.notes || "").trim() || null,
        thumbnail: itemImage,
        poId: Number(poRow?.id) || null,
        poNumber: poRow?.poNumber || null,
        sku: rowSku || null,
      });
    });

    if (!pricingRows.length) {
      if (!isReceivableWarehouseLine(item)) return;
      const orderedQuantityRaw =
        item?.quantity
        ?? item?.qty
        ?? item?.orderQuantity
        ?? item?.orderedQuantity
        ?? item?.units
        ?? null;
      const orderedQuantity = Number(orderedQuantityRaw);
      if (!Number.isFinite(orderedQuantity) || orderedQuantity <= 0) return;
      if (seenLineKeys.has(normalizeLineKey(itemId))) return;
      seenLineKeys.add(normalizeLineKey(itemId));
      lines.push({
        lineKey: normalizeLineKey(itemId),
        lineLabel: itemLabelBase || `PO Line ${itemIndex + 1}`,
        orderedQuantity,
        unit: String(item?.unit || item?.uom || "").trim() || null,
        description: String(item?.description || item?.notes || "").trim() || null,
        thumbnail: itemImage,
        poId: Number(poRow?.id) || null,
        poNumber: poRow?.poNumber || null,
        sku: itemSku || null,
      });
    }
  });

  return lines;
};

const emptyReceiptForm = {
  boxRef: "",
  refMode: "tracked",
  containerType: "box",
  containerCount: 1,
  goodsName: "",
  goodsDescription: "",
  quantity: "",
  expectedQuantity: "",
  varianceReason: "",
  unit: "pcs",
  binKey: "",
  binSubLocation: "",
  binShelfLevel: "",
  completionAction: "mark-received",
  poLineKey: "",
  poLineLabel: "",
  notes: "",
  postProjectComment: true,
};

const UNTRACKED_OPTION = "__UNTRACKED__";

export default function InstanceShippingLabels() {
  const { instanceUniqueId } = useParams();
  const { getWorkflowLabel, getWorkflowTooltip } = useWorkflowDisplaySettings(instanceUniqueId);
  const normalizePoStatus = (value) => {
    const raw = String(value || "").trim().toLowerCase();
    if (!raw) return "Unknown";
    if (raw === "supplier confirmed") return "Confirmed";
    if (raw === "shipped from supplier") return "Shipped";
    if (raw === "confirmed by shipping") return "Received";
    if (raw === "production complete" || raw === "po-prod-complete") return "Production Complete";
    if (raw === "in production") return "Partially Received";
    return String(value || "Unknown").trim();
  };
  const poLabel = (status) => {
    const canonical = normalizePoStatus(status);
    return getWorkflowLabel("PO", canonical || "Unknown") || (canonical || "Unknown");
  };
  const poTooltip = (status) => {
    const canonical = normalizePoStatus(status);
    return getWorkflowTooltip("PO", canonical || "");
  };
  const resolvePoStatusText = (row) => {
    const candidates = [
      row?.supplierStatus,
      row?.supplierStatusText,
      row?.statusLabel,
      row?.statusText,
      row?.status,
    ];
    const chosen = candidates.find((value) => {
      if (value == null) return false;
      const text = String(value).trim();
      if (!text) return false;
      return !/^\d+$/.test(text);
    });
    return chosen ? String(chosen).trim() : "Unknown";
  };
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [showReceivedHistory, setShowReceivedHistory] = useState(false);
  const [receivingEligiblePoStatuses, setReceivingEligiblePoStatuses] = useState(
    DEFAULT_RECEIVING_ELIGIBLE_PO_STATUSES
  );

  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouseKey, setSelectedWarehouseKey] = useState("main");
  const [historyWarehouseKey, setHistoryWarehouseKey] = useState(HISTORY_ALL_WAREHOUSES_KEY);

  const [warehouseLayout, setWarehouseLayout] = useState(null);
  const [warehouseLayoutKey, setWarehouseLayoutKey] = useState("");
  const [warehouseLayoutLoading, setWarehouseLayoutLoading] = useState(false);
  const [warehouseLayoutError, setWarehouseLayoutError] = useState("");
  const [receiptDialog, setReceiptDialog] = useState({ open: false, row: null });
  const [poReceipts, setPoReceipts] = useState([]);
  const [poReceiptLineSummaries, setPoReceiptLineSummaries] = useState([]);
  const [poReceiptEvents, setPoReceiptEvents] = useState([]);
  const [receiptsLoading, setReceiptsLoading] = useState(false);
  const [receiptEventsLoading, setReceiptEventsLoading] = useState(false);
  const [receiptEventsError, setReceiptEventsError] = useState("");
  const [shippingMethodOptions, setShippingMethodOptions] = useState([]);
  const [form, setForm] = useState(emptyReceiptForm);
  const [lineReceiveDrafts, setLineReceiveDrafts] = useState({});
  const [saveState, setSaveState] = useState({ saving: false, message: "", error: "" });

  const [binPickerOpen, setBinPickerOpen] = useState(false);
  const [slotSuggestions, setSlotSuggestions] = useState({ subLocation: [], shelfLevel: [] });
  const [boxRefMode, setBoxRefMode] = useState("tracked");
  const [targetPoId, setTargetPoId] = useState(null);
  const [quantityMode, setQuantityMode] = useState("add");
  const [isCorrectionMode, setIsCorrectionMode] = useState(false);
  const [lineReceiveOverrides, setLineReceiveOverrides] = useState({});
  const [lineOverrideDialog, setLineOverrideDialog] = useState({ open: false, lineKey: "" });
  const [lineOverrideForm, setLineOverrideForm] = useState({
    refMode: "tracked",
    boxRef: "",
    containerType: "box",
    containerCount: 1,
    binKey: "",
    binSubLocation: "",
    binShelfLevel: "",
  });
  const [lineOverrideRowSelectionId, setLineOverrideRowSelectionId] = useState(null);
  const [lineOverrideRowQuantity, setLineOverrideRowQuantity] = useState("");
  const [lineOverrideRowMode, setLineOverrideRowMode] = useState("set");
  const [lineOverrideSaveState, setLineOverrideSaveState] = useState({ saving: false, error: "", message: "" });
  const [receiveNotesTab, setReceiveNotesTab] = useState("additional");
  const [projectOverviewNotes, setProjectOverviewNotes] = useState([]);
  const [projectNotesFilterProductionOnly, setProjectNotesFilterProductionOnly] = useState(true);
  const [projectNotesUserFilter, setProjectNotesUserFilter] = useState("");
  const [projectNotesSearch, setProjectNotesSearch] = useState("");
  const [projectNoteInput, setProjectNoteInput] = useState("");
  const [projectNotesVisibleCount, setProjectNotesVisibleCount] = useState(20);
  const [projectNotePostState, setProjectNotePostState] = useState({ saving: false, error: "", message: "" });
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionIndex, setMentionIndex] = useState(0);
  const mentionRangeRef = useRef({ from: null, to: null });
  const noteTextareaRef = useRef(null);
  const warehouseLayoutRequestRef = useRef(0);
  const [optimisticReceivedLockPoId, setOptimisticReceivedLockPoId] = useState(null);

  const loadReceiptsForPo = async (poId) => {
    if (!Number.isFinite(Number(poId))) {
      setPoReceipts([]);
      setPoReceiptLineSummaries([]);
      return [];
    }
    setReceiptsLoading(true);
    try {
      const activeWarehouseScopeKey = showReceivedHistory ? historyWarehouseKey : selectedWarehouseKey;
      const res = await fetchPoReceipts(poId, instanceUniqueId, activeWarehouseScopeKey);
      const receipts = Array.isArray(res?.receipts) ? res.receipts : [];
      const lineSummaries = Array.isArray(res?.lineSummaries) ? res.lineSummaries : [];
      setPoReceipts(receipts);
      setPoReceiptLineSummaries(lineSummaries);
      return receipts;
    } catch {
      setPoReceipts([]);
      setPoReceiptLineSummaries([]);
      return [];
    } finally {
      setReceiptsLoading(false);
    }
  };
  const loadReceiptEventsForPo = async (poId) => {
    if (!Number.isFinite(Number(poId))) {
      setPoReceiptEvents([]);
      setReceiptEventsError("");
      return [];
    }
    setReceiptEventsLoading(true);
    setReceiptEventsError("");
    try {
      const activeWarehouseScopeKey = showReceivedHistory ? historyWarehouseKey : selectedWarehouseKey;
      const res = await fetchPoReceiptEvents(poId, instanceUniqueId, activeWarehouseScopeKey);
      const events = Array.isArray(res?.events) ? res.events : [];
      const sorted = [...events].sort((a, b) => {
        const bTime = new Date(b?.createdAt || b?.timestamp || 0).getTime();
        const aTime = new Date(a?.createdAt || a?.timestamp || 0).getTime();
        return bTime - aTime;
      });
      setPoReceiptEvents(sorted);
      return sorted;
    } catch {
      setPoReceiptEvents([]);
      setReceiptEventsError("Failed to load shipping log.");
      return [];
    } finally {
      setReceiptEventsLoading(false);
    }
  };

  const loadRows = async (options = {}) => {
    const useHistoryMode =
      Object.prototype.hasOwnProperty.call(options, "history")
        ? options.history === true
        : showReceivedHistory;
    setLoading(true);
    try {
      if (useHistoryMode) {
        const [purchaseOrdersRes, receivedHistoryRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_NODEJS_SERVER_URL}/purchase-order`, {
            params: { instanceUniqueId },
            headers: { accessToken: localStorage.getItem("accessToken") },
          }),
          fetchReceivedHistory(instanceUniqueId, historyWarehouseKey),
        ]);
        const purchaseOrders = Array.isArray(purchaseOrdersRes?.data?.purchaseOrders)
          ? purchaseOrdersRes.data.purchaseOrders
          : [];
        const receivedHistoryRows = Array.isArray(receivedHistoryRes?.rows)
          ? receivedHistoryRes.rows
          : [];
        const list = buildReceivedHistoryDisplayRows({
          purchaseOrders,
          historyRows: receivedHistoryRows,
        });
        setRows(list);
        return list;
      }

      const res = await axios.get(`${process.env.REACT_APP_NODEJS_SERVER_URL}/purchase-order`, {
        params: { instanceUniqueId, context: "shipping-label", receivableOnly: true },
        headers: { accessToken: localStorage.getItem("accessToken") },
      });
      const list = Array.isArray(res?.data?.purchaseOrders) ? res.data.purchaseOrders : [];
      setRows(list);
      return list;
    } catch (error) {
      console.error("Failed to load shipping labels", error);
      setRows([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const loadReceivingSettings = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/instances/${instanceUniqueId}/internal-shipping-settings`,
        {
          headers: { accessToken: localStorage.getItem("accessToken") },
        }
      );
      const nextStatuses = Array.isArray(response?.data?.settings?.receivingEligiblePoStatuses)
        ? response.data.settings.receivingEligiblePoStatuses
        : DEFAULT_RECEIVING_ELIGIBLE_PO_STATUSES;
      setReceivingEligiblePoStatuses(nextStatuses);
      return nextStatuses;
    } catch (error) {
      console.error("Failed to load receiving settings", error);
      setReceivingEligiblePoStatuses(DEFAULT_RECEIVING_ELIGIBLE_PO_STATUSES);
      return DEFAULT_RECEIVING_ELIGIBLE_PO_STATUSES;
    }
  };

  const loadShippingMethodOptions = async () => {
    try {
      const instanceRes = await axios.get(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/instances/byinstanceUniqueId/${instanceUniqueId}`
      );
      const instanceId = instanceRes?.data?.id;
      if (!instanceId) {
        setShippingMethodOptions([]);
        return [];
      }
      const methodsRes = await axios.get(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/instances/${instanceId}/ship-methods`,
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      const methods = Array.isArray(methodsRes?.data?.methods) ? methodsRes.data.methods : [];
      const options = methods.map((method) => ({
        id: method.id,
        name: method.name,
        courier: method.courier || "",
      }));
      setShippingMethodOptions(options);
      return options;
    } catch (error) {
      console.error("Failed to load shipping method options", error);
      setShippingMethodOptions([]);
      return [];
    }
  };

  const pickPrimaryWarehouse = (warehouseList = []) =>
    warehouseList.find((warehouse) => warehouse?.isPrimary || warehouse?.primary) || warehouseList[0] || null;

  const loadWarehouses = async () => {
    try {
      const res = await fetchWarehouses(instanceUniqueId);
      const list = Array.isArray(res?.warehouses) ? res.warehouses : [];
      setWarehouses(list);
      if (list.length && !list.some((warehouse) => warehouse.warehouseKey === selectedWarehouseKey)) {
        const fallbackWarehouse = pickPrimaryWarehouse(list);
        if (fallbackWarehouse?.warehouseKey) setSelectedWarehouseKey(fallbackWarehouse.warehouseKey);
      }
      return list;
    } catch {
      setWarehouses([]);
      return [];
    }
  };

  const loadWarehouseLayout = async (warehouseKey) => {
    const nextWarehouseKey = String(warehouseKey || "").trim();
    if (!nextWarehouseKey) return null;

    const requestId = warehouseLayoutRequestRef.current + 1;
    warehouseLayoutRequestRef.current = requestId;
    setWarehouseLayoutLoading(true);
    setWarehouseLayoutError("");
    if (warehouseLayoutKey && warehouseLayoutKey !== nextWarehouseKey) {
      setWarehouseLayout(null);
      setWarehouseLayoutKey("");
    }

    try {
      const res = await fetchWarehouseLayout(instanceUniqueId, nextWarehouseKey);
      const layout = extractWarehouseLayout(res);
      if (warehouseLayoutRequestRef.current === requestId) {
        setWarehouseLayout(layout);
        setWarehouseLayoutKey(nextWarehouseKey);
      }
      return layout;
    } catch {
      if (warehouseLayoutRequestRef.current === requestId) {
        setWarehouseLayout(null);
        setWarehouseLayoutKey(nextWarehouseKey);
        setWarehouseLayoutError("Failed to load warehouse layout.");
      }
      return null;
    } finally {
      if (warehouseLayoutRequestRef.current === requestId) {
        setWarehouseLayoutLoading(false);
      }
    }
  };

  const handleOpenBinPicker = async () => {
    setBinPickerOpen(true);
    let effectiveKey = selectedWarehouseKey || "main";
    let warehouseList = Array.isArray(warehouses) ? warehouses : [];

    if (!warehouseList.length) {
      warehouseList = await loadWarehouses();
    }

    if (warehouseList.length && !warehouseList.some((warehouse) => warehouse.warehouseKey === effectiveKey)) {
      const fallbackWarehouse = pickPrimaryWarehouse(warehouseList);
      if (fallbackWarehouse?.warehouseKey) effectiveKey = fallbackWarehouse.warehouseKey;
    }

    if (effectiveKey !== selectedWarehouseKey) {
      setSelectedWarehouseKey(effectiveKey);
    }

    if (!warehouseLayout || warehouseLayoutKey !== effectiveKey) {
      await loadWarehouseLayout(effectiveKey);
    }
  };

  useEffect(() => {
    loadRows({ history: showReceivedHistory });
    loadReceivingSettings();
    loadWarehouses();
    loadShippingMethodOptions();
  }, [instanceUniqueId, showReceivedHistory]);

  useEffect(() => {
    loadWarehouseLayout(selectedWarehouseKey);
  }, [instanceUniqueId, selectedWarehouseKey]);

  useEffect(() => {
    if (!showReceivedHistory) return;
    loadRows({ history: true });
  }, [historyWarehouseKey]);

  useEffect(() => {
    if (!showReceivedHistory) return;
    setHistoryWarehouseKey((current) => current || HISTORY_ALL_WAREHOUSES_KEY);
  }, [showReceivedHistory]);

  const visibleWarehouseKey = showReceivedHistory ? historyWarehouseKey : selectedWarehouseKey;

  const filteredRows = useMemo(() => {
    return filterReceivingQueueRows({ rows, query });
  }, [rows, query]);

  const binOptions = useMemo(
    () => (warehouseLayoutKey === selectedWarehouseKey ? parseWarehouseBins(warehouseLayout) : []),
    [warehouseLayout, warehouseLayoutKey, selectedWarehouseKey]
  );

  const loadSlotSuggestionsForBin = async (binOption) => {
    if (!binOption) {
      setSlotSuggestions({ subLocation: [], shelfLevel: [] });
      return;
    }
    try {
      const res = await fetchBinReceipts({
        instanceUniqueId,
        warehouseKey: selectedWarehouseKey,
        warehouseLevel: binOption.warehouseLevel,
        warehouseCell: binOption.warehouseCell,
      });
      const receipts = Array.isArray(res?.receipts) ? res.receipts : [];
      const subLocation = [...new Set(receipts.map((receipt) => String(receipt?.binSubLocation || "").trim()).filter(Boolean))];
      const shelfLevel = [...new Set(receipts.map((receipt) => String(receipt?.binShelfLevel || "").trim()).filter(Boolean))];
      setSlotSuggestions({ subLocation, shelfLevel });
    } catch {
      setSlotSuggestions({ subLocation: [], shelfLevel: [] });
    }
  };

  const applyExistingReceiptToForm = (receipt, fallbackBoxRef) => {
    const selectedBin = receipt
      ? binOptions.find((bin) => bin.warehouseLevel === receipt.warehouseLevel && bin.warehouseCell === receipt.warehouseCell)
      : null;

    setForm((prev) => ({
      ...prev,
      boxRef: fallbackBoxRef || receipt?.boxRef || prev.boxRef,
      refMode: receipt?.refMode || prev.refMode,
      containerType: receipt?.containerType || "box",
      containerCount: receipt?.containerCount || 1,
      goodsName: receipt?.goodsName || "",
      goodsDescription: receipt?.goodsDescription || "",
      quantity: receipt?.quantity ?? "",
      expectedQuantity: receipt?.expectedQuantity ?? "",
      varianceReason: receipt?.varianceReason || "",
      unit: receipt?.unit || "pcs",
      binKey: selectedBin?.key || "",
      binSubLocation: receipt?.binSubLocation || "",
      binShelfLevel: receipt?.binShelfLevel || "",
      completionAction: "mark-received",
      poLineKey: receipt?.poLineKey || "",
      poLineLabel: receipt?.poLineLabel || "",
      notes: receipt?.notes || "",
    }));

    loadSlotSuggestionsForBin(selectedBin || null);
  };

  const openReceiveDialog = async (row) => {
    const tracking = parseTrackingList(row?.trackingNumbers);
    const hasTrackedRefs = tracking.length > 0;
    const boxRef = hasTrackedRefs ? tracking[0] : "";

    setReceiptDialog({ open: true, row });
    setTargetPoId(Number(row?.id) || null);
    setQuantityMode("add");
    setIsCorrectionMode(false);
    setOptimisticReceivedLockPoId(null);
    setLineReceiveOverrides({});
    setLineOverrideDialog({ open: false, lineKey: "" });
    setPoReceipts([]);
    setPoReceiptEvents([]);
    setLineReceiveDrafts({});
    const lineItems = parsePoLineItems(row);
    const initialLine = lineItems[0] || null;
    setForm({
      ...emptyReceiptForm,
      boxRef,
      refMode: hasTrackedRefs ? "tracked" : "untracked",
      poLineKey: initialLine?.lineKey || "",
      poLineLabel: initialLine?.lineLabel || "",
      expectedQuantity: initialLine?.orderedQuantity ?? "",
      unit: initialLine?.unit || emptyReceiptForm.unit,
    });
    setBoxRefMode(hasTrackedRefs ? "tracked" : "untracked");
    setSaveState({ saving: false, message: "", error: "" });
    setSlotSuggestions({ subLocation: [], shelfLevel: [] });

    const receipts = await loadReceiptsForPo(Number(row?.id));
    await loadReceiptEventsForPo(Number(row?.id));
    const targetRef = normalizeTrackingRef(boxRef);
    const existing = receipts.find((receipt) => normalizeTrackingRef(receipt?.boxRef) === targetRef);
    if (existing) applyExistingReceiptToForm(existing, boxRef);
  };

  const closeDialog = () => {
    setReceiptDialog({ open: false, row: null });
    setTargetPoId(null);
    setPoReceipts([]);
    setPoReceiptLineSummaries([]);
    setPoReceiptEvents([]);
    setReceiptEventsError("");
    setLineReceiveDrafts({});
    setForm(emptyReceiptForm);
    setSaveState({ saving: false, message: "", error: "" });
    setBinPickerOpen(false);
    setSlotSuggestions({ subLocation: [], shelfLevel: [] });
    setBoxRefMode("tracked");
    setQuantityMode("add");
    setIsCorrectionMode(false);
    setOptimisticReceivedLockPoId(null);
    setLineReceiveOverrides({});
    setLineOverrideDialog({ open: false, lineKey: "" });
  };

  const selectedPo = receiptDialog.row;
  const projectPoRows = useMemo(
    () => (selectedPo?.orderId ? rows.filter((row) => String(row?.orderId || "") === String(selectedPo.orderId)) : []),
    [rows, selectedPo]
  );
  const activePo = useMemo(
    () => projectPoRows.find((row) => Number(row?.id) === Number(targetPoId)) || selectedPo,
    [projectPoRows, selectedPo, targetPoId]
  );
  const mentionUsers = useMemo(() => {
    const pools = [
      activePo?.userDirectory,
      selectedPo?.userDirectory,
      activePo?.allUsers,
      selectedPo?.allUsers,
      activePo?.orderAssignedUsers,
      selectedPo?.orderAssignedUsers,
      activePo?.salesOrder?.assignedTo,
      selectedPo?.salesOrder?.assignedTo,
      activePo?.assignedTo,
      selectedPo?.assignedTo,
    ].filter(Boolean);
    const normalize = (u = {}) => {
      const id = u?.id ?? u?._id ?? u?.userId;
      const email = String(u?.email || "").trim();
      const first = String(u?.first_name || u?.firstName || "").trim();
      const last = String(u?.last_name || u?.lastName || "").trim();
      const name = String(u?.name || `${first} ${last}`.trim() || email || "").trim();
      if (!id && !email) return null;
      return { id: String(id || email), name: name || email || `User ${id}`, email, avatar: u?.avatar || u?.profilePicture || null };
    };
    const all = pools.flatMap((pool) => (Array.isArray(pool) ? pool : []))
      .map(normalize)
      .filter(Boolean);
    const seen = new Set();
    return all.filter((u) => {
      const key = `${String(u.id).toLowerCase()}|${String(u.email).toLowerCase()}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
  }, [activePo, selectedPo]);
  const activeOrderId = String(activePo?.orderId || selectedPo?.orderId || "").trim();
  const normalizedActivePoStatus = String(normalizePoStatus(activePo?.supplierStatus || "") || "").toLowerCase();
  const normalizedEligiblePoStatuses = useMemo(
    () =>
      new Set(
        (Array.isArray(receivingEligiblePoStatuses) ? receivingEligiblePoStatuses : [])
          .map((status) => String(normalizePoStatus(status) || "").toLowerCase())
          .filter(Boolean)
      ),
    [receivingEligiblePoStatuses]
  );
  const activePoId = Number(activePo?.id);
  const isOptimisticReceivedLock = Number.isFinite(activePoId) && Number(optimisticReceivedLockPoId) === activePoId;
  const isReceivedStatus = normalizedActivePoStatus === "received" || isOptimisticReceivedLock;
  const isReceivableStatus =
    Boolean(normalizedActivePoStatus) &&
    normalizedEligiblePoStatuses.has(normalizedActivePoStatus) &&
    !isReceivedStatus;
  const isHistoryLockedByStatus =
    showReceivedHistory && !isReceivableStatus && !isReceivedStatus;
  const isReadOnly = (isReceivedStatus || isHistoryLockedByStatus) && !isCorrectionMode;
  const projectPoIds = useMemo(() => projectPoRows.map((row) => Number(row?.id)).filter((id) => Number.isInteger(id)), [projectPoRows]);
  const projectReceiptsForLoadedPo = useMemo(
    () => poReceipts.filter((receipt) => projectPoIds.includes(Number(receipt?.poId))),
    [poReceipts, projectPoIds]
  );
  const projectExpectedTrackedContainers = useMemo(
    () => projectPoRows.reduce((sum, row) => sum + parseTrackingCount(row?.trackingNumbers), 0),
    [projectPoRows]
  );
  const projectTrackedRefsReceived = useMemo(
    () => new Set(projectReceiptsForLoadedPo.map((receipt) => normalizeTrackingRef(receipt?.boxRef)).filter(Boolean)).size,
    [projectReceiptsForLoadedPo]
  );

  const selectedBinOption = binOptions.find((bin) => bin.key === form.binKey);
  const poLineItems = useMemo(() => parsePoLineItems(activePo), [activePo]);
  const selectedLineItem = useMemo(
    () => poLineItems.find((item) => normalizeLineKey(item.lineKey) === normalizeLineKey(form.poLineKey)) || null,
    [poLineItems, form.poLineKey]
  );

  const trackingList = useMemo(() => parseTrackingList(activePo?.trackingNumbers), [activePo]);
  const expectedTrackedContainers = trackingList.length;
  const receivedBoxRefs = useMemo(
    () => uniqueTrackingRefs(poReceipts.map((receipt) => receipt?.boxRef)),
    [poReceipts]
  );
  const receivedBoxCount = receivedBoxRefs.length;
  const receivedUnits = useMemo(() => poReceipts.reduce((sum, receipt) => sum + (Number(receipt?.quantity) || 0), 0), [poReceipts]);
  const goodsImageUrl = useMemo(() => resolvePoGoodsImage(activePo), [activePo]);

  const trackedRefs = useMemo(() => parseTrackingList(activePo?.trackingNumbers), [activePo]);
  const trackedRefOptions = useMemo(() => uniqueTrackingRefs([...trackedRefs, ...receivedBoxRefs]), [trackedRefs, receivedBoxRefs]);
  const varianceQty = useMemo(() => {
    const expected = Number(form.expectedQuantity);
    const received = Number(form.quantity);
    if (!Number.isFinite(expected)) return null;
    return (Number.isFinite(received) ? received : 0) - expected;
  }, [form.expectedQuantity, form.quantity]);
  const receivedLineUnits = useMemo(() => {
    if (!form.poLineKey) return 0;
    return poReceipts
      .filter((receipt) => normalizeLineKey(receipt?.poLineKey) === normalizeLineKey(form.poLineKey))
      .reduce((sum, receipt) => sum + (Number(receipt?.quantity) || 0), 0);
  }, [poReceipts, form.poLineKey]);
  const lineRemainingQty = useMemo(() => {
    const ordered = Number(selectedLineItem?.orderedQuantity);
    if (!Number.isFinite(ordered)) return null;
    return ordered - receivedLineUnits;
  }, [selectedLineItem, receivedLineUnits]);
  const receivedByLineKey = useMemo(() => {
    const map = {};
    if (Array.isArray(poReceiptLineSummaries) && poReceiptLineSummaries.length) {
      poReceiptLineSummaries.forEach((summary) => {
        const key = normalizeLineKey(summary?.poLineKey);
        if (!key) return;
        map[key] = Number(summary?.netReceivedQuantity || 0) || 0;
      });
      return map;
    }
    poReceipts.forEach((receipt) => {
      const key = normalizeLineKey(receipt?.poLineKey);
      if (!key) return;
      map[key] = (Number(map[key]) || 0) + (Number(receipt?.quantityExact ?? receipt?.quantity) || 0);
    });
    return map;
  }, [poReceipts, poReceiptLineSummaries]);
  const lineItemsForReceiveTable = useMemo(
    () =>
      poLineItems.map((line) => {
        const lineKey = normalizeLineKey(line.lineKey);
        const receivedQuantity = Number(receivedByLineKey[lineKey] || 0);
        const ordered = Number(line?.orderedQuantity);
        const remainingQuantity = Number.isFinite(ordered) ? ordered - receivedQuantity : null;
        const hasDraft = Object.prototype.hasOwnProperty.call(lineReceiveDrafts, lineKey);
        const draftReceiveQuantity = hasDraft
          ? lineReceiveDrafts[lineKey]
          : (remainingQuantity == null ? "" : Math.max(remainingQuantity, 0));
        const targetReceived = hasDraft
          ? lineReceiveDrafts[lineKey]
          : String(receivedQuantity);
        const rowInputValue = isCorrectionMode ? targetReceived : draftReceiveQuantity;
        const rowInputNumber = Number(rowInputValue);
        const deltaForSave = isCorrectionMode
          ? (Number.isFinite(rowInputNumber) ? rowInputNumber - receivedQuantity : 0)
          : rowInputNumber;
        return {
          ...line,
          currentReceived: receivedQuantity,
          receivedQuantity,
          remainingQuantity,
          lineVarianceQuantity: (Number.isFinite(ordered) ? receivedQuantity - ordered : null),
          draftReceiveQuantity,
          maxReceivableNow: remainingQuantity == null ? null : Math.max(remainingQuantity, 0),
          targetReceived,
          rowInputValue,
          deltaForSave,
        };
      }),
    [poLineItems, receivedByLineKey, lineReceiveDrafts, isCorrectionMode]
  );
  const selectedLineForReceiveTable = useMemo(
    () => lineItemsForReceiveTable.find((line) => normalizeLineKey(line.lineKey) === normalizeLineKey(form.poLineKey)) || null,
    [lineItemsForReceiveTable, form.poLineKey]
  );
  const lineHasOverride = (lineKey) => Boolean(lineReceiveOverrides?.[lineKey]?.enabled);
  const getEffectiveLineReceiptConfig = (line) => {
    const override = lineReceiveOverrides?.[line?.lineKey];
    if (!override?.enabled) {
      return {
        refMode: boxRefMode,
        boxRef: form.boxRef,
        containerType: form.containerType,
        containerCount: form.containerCount,
        binKey: form.binKey,
        binSubLocation: form.binSubLocation,
        binShelfLevel: form.binShelfLevel,
      };
    }
    return {
      refMode: override.refMode || boxRefMode,
      boxRef: override.boxRef ?? form.boxRef,
      containerType: override.containerType || form.containerType,
      containerCount: override.containerCount || form.containerCount,
      binKey: override.binKey ?? form.binKey,
      binSubLocation: override.binSubLocation ?? form.binSubLocation,
      binShelfLevel: override.binShelfLevel ?? form.binShelfLevel,
    };
  };
  const activeLineOverride = useMemo(
    () => (lineOverrideDialog.lineKey ? lineReceiveOverrides?.[lineOverrideDialog.lineKey] || null : null),
    [lineOverrideDialog.lineKey, lineReceiveOverrides]
  );

  const activeReceipt = useMemo(
    () =>
      poReceipts.find(
        (receipt) =>
          normalizeTrackingRef(receipt?.boxRef) === normalizeTrackingRef(form.boxRef) &&
          String(receipt.binSubLocation || "") === String(form.binSubLocation || "") &&
          String(receipt.binShelfLevel || "") === String(form.binShelfLevel || "")
      ),
    [poReceipts, form.boxRef, form.binSubLocation, form.binShelfLevel]
  );

  const handlePickBinFromMap = (binOption) => {
    setForm((prev) => ({ ...prev, binKey: binOption.key }));
    setBinPickerOpen(false);
    loadSlotSuggestionsForBin(binOption);
  };

  const applyLineReceiveSelection = (line, draftQty) => {
    if (!line) return;
    const draftNumeric = Number(draftQty);
    const deltaForSave = isCorrectionMode
      ? (Number.isFinite(draftNumeric) ? draftNumeric - (Number(line?.receivedQuantity) || 0) : 0)
      : draftQty;
    setForm((prev) => ({
      ...prev,
      poLineKey: line.lineKey,
      poLineLabel: line.lineLabel || "",
      goodsName: line.lineLabel || prev.goodsName,
      goodsDescription: line.description || prev.goodsDescription,
      expectedQuantity: line.orderedQuantity ?? prev.expectedQuantity,
      unit: line.unit || prev.unit,
      quantity: deltaForSave ?? prev.quantity,
    }));
  };

  const handleLineReceiveQtyChange = (line, nextQtyRaw) => {
    const nextQty = String(nextQtyRaw ?? "");
    setLineReceiveDrafts((prev) => ({ ...prev, [line.lineKey]: nextQty }));
    applyLineReceiveSelection(line, nextQty);
  };

  const handleLineFullyReceivedToggle = (line, checked) => {
    const remaining = Number(line?.remainingQuantity);
    const ordered = Number(line?.orderedQuantity);
    const nextQty = isCorrectionMode
      ? (checked
        ? String(Number.isFinite(ordered) ? Math.max(ordered, 0) : (Number(line?.currentReceived) || 0))
        : String(Number(line?.currentReceived) || 0))
      : (checked
        ? String(Number.isFinite(remaining) ? Math.max(remaining, 0) : "")
        : "");
    handleLineReceiveQtyChange(line, nextQty);
  };
  const openLineOverrideDialog = (line) => {
    if (!line) return;
    const override = lineReceiveOverrides?.[line.lineKey];
    const effective = getEffectiveLineReceiptConfig(line);
    setLineOverrideDialog({ open: true, lineKey: line.lineKey });
    setLineOverrideForm({
      refMode: override?.refMode || effective.refMode || boxRefMode,
      boxRef: override?.boxRef ?? effective.boxRef ?? "",
      containerType: override?.containerType || effective.containerType || "box",
      containerCount: override?.containerCount || effective.containerCount || 1,
      binKey: override?.binKey ?? effective.binKey ?? "",
      binSubLocation: override?.binSubLocation ?? effective.binSubLocation ?? "",
      binShelfLevel: override?.binShelfLevel ?? effective.binShelfLevel ?? "",
    });
    setLineOverrideRowSelectionId(null);
    setLineOverrideRowQuantity("");
    setLineOverrideRowMode("set");
    setLineOverrideSaveState({ saving: false, error: "", message: "" });
  };
  const closeLineOverrideDialog = () => {
    setLineOverrideDialog({ open: false, lineKey: "" });
    setLineOverrideRowSelectionId(null);
    setLineOverrideRowQuantity("");
    setLineOverrideRowMode("set");
    setLineOverrideSaveState({ saving: false, error: "", message: "" });
  };
  const saveLineOverride = async () => {
    const lineKey = lineOverrideDialog.lineKey;
    if (!lineKey) return;
    if (Number.isFinite(Number(lineOverrideRowSelectionId)) && Number(lineOverrideRowSelectionId) > 0) {
      const selectedRow = poReceipts.find((receipt) => Number(receipt?.id) === Number(lineOverrideRowSelectionId));
      const selectedPoId = Number(targetPoId || selectedPo?.id);
      const selectedLine = poLineItems.find((line) => normalizeLineKey(line.lineKey) === normalizeLineKey(lineKey)) || null;
      const quantityValue = Number(lineOverrideRowQuantity);
      const lineBinOption = binOptions.find((bin) => bin.key === lineOverrideForm.binKey) || null;
      if (!selectedRow) return setLineOverrideSaveState({ saving: false, error: "Selected receipt row was not found.", message: "" });
      if (!Number.isFinite(selectedPoId)) return setLineOverrideSaveState({ saving: false, error: "No active PO selected.", message: "" });
      if (!Number.isFinite(quantityValue) || quantityValue < 0) return setLineOverrideSaveState({ saving: false, error: "Enter a valid receipt row quantity.", message: "" });
      if (!lineBinOption) return setLineOverrideSaveState({ saving: false, error: "Select a valid warehouse bin.", message: "" });
      const normalizedRefMode = lineOverrideForm.refMode === "untracked" ? "untracked" : "tracked";
      const nextBoxRef = String(lineOverrideForm.boxRef || "").trim();
      if (!nextBoxRef) return setLineOverrideSaveState({ saving: false, error: "Tracking/container reference is required.", message: "" });
      const normalized = (value) => String(value || "").trim().toLowerCase();
      const destinationChanged =
        normalized(selectedRow?.boxRef) !== normalized(nextBoxRef)
        || normalized(selectedRow?.warehouseLevel) !== normalized(lineBinOption?.warehouseLevel)
        || normalized(selectedRow?.warehouseCell) !== normalized(lineBinOption?.warehouseCell)
        || normalized(selectedRow?.binSubLocation) !== normalized(lineOverrideForm.binSubLocation)
        || normalized(selectedRow?.binShelfLevel) !== normalized(lineOverrideForm.binShelfLevel);
      if (destinationChanged && lineOverrideRowMode === "add") {
        setLineOverrideSaveState({
          saving: false,
          error: "Destination changed. Switch row mode to 'Set row quantity' to save this move.",
          message: "",
        });
        return;
      }
      try {
        setLineOverrideSaveState({ saving: true, error: "", message: "" });
        if (destinationChanged) {
          await savePoReceipt(selectedPoId, {
            instanceUniqueId,
            warehouseKey: selectedWarehouseKey,
            refMode: String(selectedRow?.refMode || "tracked").toLowerCase() === "untracked" ? "untracked" : "tracked",
            boxRef: selectedRow?.boxRef,
            containerType: selectedRow?.containerType || "box",
            containerCount: Number(selectedRow?.containerCount) || 1,
            goodsName: selectedLine?.lineLabel || selectedRow?.goodsName || "",
            poLineKey: lineKey,
            poLineLabel: selectedLine?.lineLabel || selectedRow?.poLineLabel || "",
            goodsDescription: selectedLine?.description || selectedRow?.goodsDescription || "",
            quantity: 0,
            expectedQuantity: selectedLine?.orderedQuantity ?? selectedRow?.expectedQuantity ?? "",
            quantityMode: "set",
            allowCorrection: true,
            varianceReason: form.varianceReason,
            unit: selectedLine?.unit || selectedRow?.unit || form.unit,
            warehouseLevel: selectedRow?.warehouseLevel || null,
            warehouseCell: selectedRow?.warehouseCell || null,
            binLabel: selectedRow?.binLabel || null,
            binSubLocation: selectedRow?.binSubLocation || "",
            binShelfLevel: selectedRow?.binShelfLevel || "",
            metadataBaselineReceiptId: Number(selectedRow?.id) || null,
            completionAction: "mark-received",
            notes: form.notes,
            postProjectComment: false,
          });
        }
        await savePoReceipt(selectedPoId, {
          instanceUniqueId,
          warehouseKey: selectedWarehouseKey,
          refMode: normalizedRefMode,
          boxRef: nextBoxRef,
          containerType: lineOverrideForm.containerType || "box",
          containerCount: Number(lineOverrideForm.containerCount) || 1,
          goodsName: selectedLine?.lineLabel || form.goodsName,
          poLineKey: lineKey,
          poLineLabel: selectedLine?.lineLabel || form.poLineLabel,
          goodsDescription: selectedLine?.description || form.goodsDescription,
          quantity: quantityValue,
          expectedQuantity: selectedLine?.orderedQuantity ?? form.expectedQuantity,
          quantityMode: lineOverrideRowMode === "add" ? "add" : "set",
          allowCorrection: true,
          varianceReason: form.varianceReason,
          unit: selectedLine?.unit || form.unit,
          warehouseLevel: lineBinOption?.warehouseLevel || null,
          warehouseCell: lineBinOption?.warehouseCell || null,
          binLabel: lineBinOption?.label || null,
          binSubLocation: lineOverrideForm.binSubLocation,
          binShelfLevel: lineOverrideForm.binShelfLevel,
          metadataBaselineReceiptId: destinationChanged ? null : (Number(selectedRow?.id) || null),
          completionAction: "mark-received",
          notes: form.notes,
          postProjectComment: false,
        });
        await loadReceiptsForPo(selectedPoId);
        await loadReceiptEventsForPo(selectedPoId);
        const latestRows = await loadRows();
        const refreshedCurrentPo = (latestRows || []).find((row) => Number(row?.id) === selectedPoId) || null;
        if (refreshedCurrentPo) {
          setReceiptDialog((prev) => ({ ...prev, row: refreshedCurrentPo }));
        }
        setLineOverrideSaveState({ saving: false, error: "", message: "Receipt row updated." });
      } catch (error) {
        setLineOverrideSaveState({ saving: false, error: error?.response?.data?.message || "Failed to update receipt row.", message: "" });
      }
      return;
    }
    setLineReceiveOverrides((prev) => ({
      ...prev,
      [lineKey]: {
        enabled: true,
        refMode: lineOverrideForm.refMode,
        boxRef: lineOverrideForm.boxRef,
        containerType: lineOverrideForm.containerType,
        containerCount: Number(lineOverrideForm.containerCount) || 1,
        binKey: lineOverrideForm.binKey,
        binSubLocation: lineOverrideForm.binSubLocation,
        binShelfLevel: lineOverrideForm.binShelfLevel,
      },
    }));
    closeLineOverrideDialog();
  };
  const clearLineOverride = () => {
    const lineKey = lineOverrideDialog.lineKey;
    if (!lineKey) return;
    setLineReceiveOverrides((prev) => {
      const next = { ...prev };
      delete next[lineKey];
      return next;
    });
    closeLineOverrideDialog();
  };

  const computeLineSaveQuantity = (line) => {
    if (!line) return Number(form.quantity);
    const numeric = Number(line.rowInputValue);
    if (!Number.isFinite(numeric)) return Number(form.quantity);
    return isCorrectionMode ? (numeric - (Number(line?.currentReceived) || 0)) : numeric;
  };
  const isPoFullyReceivedFromReceipts = (poRow, receipts) => {
    const lines = parsePoLineItems(poRow);
    if (!lines.length) return false;
    const receivedByKey = {};
    (Array.isArray(receipts) ? receipts : []).forEach((receipt) => {
      const key = normalizeLineKey(receipt?.poLineKey);
      if (!key) return;
      receivedByKey[key] = (Number(receivedByKey[key]) || 0) + (Number(receipt?.quantity) || 0);
    });
    return lines.every((line) => {
      const ordered = Number(line?.orderedQuantity);
      if (!Number.isFinite(ordered) || ordered <= 0) return true;
      const received = Number(receivedByKey[normalizeLineKey(line.lineKey)] || 0);
      return received >= ordered;
    });
  };

  const handleSaveReceipt = async () => {
    const savePoId = Number(targetPoId || selectedPo?.id);
    if (!Number.isFinite(savePoId)) return;
    const canSaveByStatus = isReceivableStatus || (isReceivedStatus && isCorrectionMode);
    if (!canSaveByStatus) {
      setSaveState({
        saving: false,
        message: "",
        error: "This purchase order is not eligible for receiving in its current status.",
      });
      return;
    }

    const normalizedBoxRef = String(form.boxRef || "").trim();
    if (!normalizedBoxRef && !Object.keys(lineReceiveOverrides || {}).length) {
      setSaveState({ saving: false, message: "", error: "Please enter a tracking/container reference." });
      return;
    }
    const receiveQty = selectedLineForReceiveTable ? computeLineSaveQuantity(selectedLineForReceiveTable) : Number(form.quantity);
    const effectiveConfig = selectedLineForReceiveTable ? getEffectiveLineReceiptConfig(selectedLineForReceiveTable) : {
      containerType: form.containerType,
      containerCount: form.containerCount,
      binKey: form.binKey,
      binSubLocation: form.binSubLocation,
      binShelfLevel: form.binShelfLevel,
    };
    const hasValidBinSelection = Boolean(effectiveConfig.binKey) && binOptions.some((bin) => bin.key === effectiveConfig.binKey);
    if (!hasValidBinSelection) {
      setSaveState({ saving: false, message: "", error: "Please select a warehouse bin before saving receipt." });
      return;
    }
    if (selectedLineForReceiveTable && lineHasOverride(selectedLineForReceiveTable.lineKey)) {
      const hasValidBin = Boolean(effectiveConfig.binKey) && binOptions.some((bin) => bin.key === effectiveConfig.binKey);
      if (!hasValidBin) {
        setSaveState({
          saving: false,
          message: "",
          error: `Select a valid override warehouse bin for "${selectedLineForReceiveTable.lineLabel}".`,
        });
        return;
      }
    }
    const effectiveBinOption = binOptions.find((bin) => bin.key === effectiveConfig.binKey) || null;
    const selectedLineKey = normalizeLineKey(selectedLineForReceiveTable?.lineKey);
    const baselineReceipt = selectedLineForReceiveTable
      ? (
        poReceipts.find((receipt) =>
          normalizeLineKey(receipt?.poLineKey) === selectedLineKey
          && String(receipt?.boxRef || "").trim() === normalizedBoxRef
        )
        || poReceipts.find((receipt) => normalizeLineKey(receipt?.poLineKey) === selectedLineKey)
        || activeReceipt
      )
      : activeReceipt;
    const normalizeValue = (value) => String(value || "").trim();
    const metadataChanged = baselineReceipt
      ? (
        normalizeValue(baselineReceipt?.boxRef) !== normalizedBoxRef
        || normalizeValue(baselineReceipt?.containerType) !== normalizeValue(effectiveConfig.containerType)
        || Number(baselineReceipt?.containerCount || 1) !== Number(effectiveConfig.containerCount || 1)
        || normalizeValue(baselineReceipt?.warehouseLevel) !== normalizeValue(effectiveBinOption?.warehouseLevel)
        || normalizeValue(baselineReceipt?.warehouseCell) !== normalizeValue(effectiveBinOption?.warehouseCell)
        || normalizeValue(baselineReceipt?.binLabel) !== normalizeValue(effectiveBinOption?.label)
        || normalizeValue(baselineReceipt?.binSubLocation) !== normalizeValue(effectiveConfig.binSubLocation)
        || normalizeValue(baselineReceipt?.binShelfLevel) !== normalizeValue(effectiveConfig.binShelfLevel)
      )
      : false;
    const isZeroQuantityMetadataUpdate = receiveQty === 0 && metadataChanged;
    const candidateLines = lineItemsForReceiveTable
      .map((line) => ({ line, qty: computeLineSaveQuantity(line) }))
      .filter(({ qty }) => Number.isFinite(qty) && qty !== 0);
    if (!Number.isFinite(receiveQty) || (!isCorrectionMode && receiveQty < 0) || (!isCorrectionMode && receiveQty === 0 && !isZeroQuantityMetadataUpdate && !candidateLines.length)) {
      if (receiveQty === 0 && !isZeroQuantityMetadataUpdate) {
        setSaveState({ saving: false, message: "", error: "No quantity or storage/container changes detected." });
        return;
      }
      setSaveState({ saving: false, message: "", error: isCorrectionMode ? "Enter a valid correction quantity." : "Enter a receive quantity greater than 0." });
      return;
    }
    const validateLineQuantity = (line, quantityToSave) => {
      const ordered = Number(line?.orderedQuantity);
      const received = Number(line?.currentReceived) || 0;
      if (isCorrectionMode) {
        const targetTotal = Number(line?.rowInputValue);
        if (!Number.isFinite(targetTotal) || targetTotal < 0) {
          return `Enter a valid correction quantity for "${line?.lineLabel || "line"}".`;
        }
        return "";
      }
      if (!Number.isFinite(quantityToSave) || quantityToSave < 0) {
        return `Enter a valid receive quantity for "${line?.lineLabel || "line"}".`;
      }
      if (Number.isFinite(ordered) && quantityToSave > Math.max(ordered - received, 0)) {
        return `Receive quantity exceeds remaining units for "${line.lineLabel}". Remaining: ${Math.max(ordered - received, 0)}.`;
      }
      return "";
    };

    setSaveState({ saving: true, message: "", error: "" });
    try {
      const linesToSave = candidateLines.length
        ? candidateLines
        : (selectedLineForReceiveTable ? [{ line: selectedLineForReceiveTable, qty: receiveQty }] : []);
      if (!linesToSave.length && !isZeroQuantityMetadataUpdate) {
        setSaveState({ saving: false, message: "", error: "No quantity or storage/container changes detected." });
        return;
      }

      for (const [lineIndex, { line, qty }] of linesToSave.entries()) {
        const lineKey = normalizeLineKey(line?.lineKey);
        if (!lineKey) {
          throw new Error(`Unable to receive "${line?.lineLabel || "line"}": missing line key.`);
        }
        const quantityError = validateLineQuantity(line, qty);
        if (quantityError) {
          throw new Error(quantityError);
        }
        const lineConfig = getEffectiveLineReceiptConfig(line);
        const lineRefMode = String(lineConfig.refMode || boxRefMode).trim().toLowerCase() === "untracked" ? "untracked" : "tracked";
        const lineBoxRef = String(lineConfig.boxRef ?? "").trim() || normalizedBoxRef;
        if (!lineBoxRef) {
          throw new Error(`Please enter a container / tracking reference for "${line?.lineLabel || "line"}".`);
        }
        const lineBinOption = binOptions.find((bin) => bin.key === lineConfig.binKey) || null;
        const lineBaselineReceipt = poReceipts.find((receipt) =>
          normalizeLineKey(receipt?.poLineKey) === lineKey
          && normalizeTrackingRef(receipt?.boxRef) === normalizeTrackingRef(lineBoxRef)
        ) || poReceipts.find((receipt) => normalizeLineKey(receipt?.poLineKey) === lineKey) || null;

        await savePoReceipt(savePoId, {
          instanceUniqueId,
          warehouseKey: selectedWarehouseKey,
          refMode: lineRefMode,
          boxRef: lineBoxRef,
          containerType: lineConfig.containerType,
          containerCount: Number(lineConfig.containerCount) || 1,
          goodsName: line.lineLabel || form.goodsName,
          poLineKey: lineKey,
          poLineLabel: line.lineLabel || form.poLineLabel,
          goodsDescription: line.description || form.goodsDescription,
          quantity: qty,
          expectedQuantity: line.orderedQuantity,
          quantityMode: isCorrectionMode ? "add" : quantityMode,
          allowCorrection: Boolean(isReceivedStatus && isCorrectionMode),
          varianceReason: form.varianceReason,
          unit: line.unit || form.unit,
          warehouseLevel: lineBinOption?.warehouseLevel || null,
          warehouseCell: lineBinOption?.warehouseCell || null,
          binLabel: lineBinOption?.label || null,
          binSubLocation: lineConfig.binSubLocation,
          binShelfLevel: lineConfig.binShelfLevel,
          metadataBaselineReceiptId: (qty === 0 && lineBaselineReceipt) ? Number(lineBaselineReceipt?.id) || null : null,
          completionAction: "mark-received",
          notes: form.notes,
          postProjectComment: Boolean(form.postProjectComment),
          receivingBatchComplete: lineIndex === linesToSave.length - 1,
        });
      }

      setSaveState({ saving: false, message: "Receipt saved.", error: "" });
      setLineReceiveDrafts({});
      const latestReceipts = await loadReceiptsForPo(savePoId);
      await loadReceiptEventsForPo(savePoId);
      const shouldOptimisticallyLock = !isCorrectionMode && isPoFullyReceivedFromReceipts(activePo, latestReceipts);
      if (shouldOptimisticallyLock) {
        setOptimisticReceivedLockPoId(savePoId);
      }
      const latestRows = await loadRows();
      const refreshedCurrentPo = (latestRows || []).find((row) => Number(row?.id) === savePoId) || null;
      if (refreshedCurrentPo) {
        setReceiptDialog((prev) => ({ ...prev, row: refreshedCurrentPo }));
        const refreshedStatus = String(normalizePoStatus(refreshedCurrentPo?.supplierStatus || "") || "").toLowerCase();
        if (refreshedStatus !== "received") {
          setOptimisticReceivedLockPoId(null);
        }
      } else {
        setOptimisticReceivedLockPoId(null);
      }
      setIsCorrectionMode(false);
    } catch (error) {
      const message = error?.response?.data?.message || "Failed to save receipt.";
      setSaveState({ saving: false, message: "", error: message });
    }
  };

  const lineOverrideExistingRows = useMemo(() => {
    const lineKey = normalizeLineKey(lineOverrideDialog.lineKey);
    if (!lineKey) return [];
    return poReceipts
      .filter((receipt) => normalizeLineKey(receipt?.poLineKey) === lineKey)
      .slice()
      .sort((a, b) => {
        const bTime = new Date(b?.updatedAt || b?.receivedAt || 0).getTime();
        const aTime = new Date(a?.updatedAt || a?.receivedAt || 0).getTime();
        return bTime - aTime;
      });
  }, [poReceipts, lineOverrideDialog.lineKey]);
  const lineOverrideSelectedRow = useMemo(
    () => lineOverrideExistingRows.find((receipt) => Number(receipt?.id) === Number(lineOverrideRowSelectionId)) || null,
    [lineOverrideExistingRows, lineOverrideRowSelectionId]
  );
  const lineOverrideDestinationChanged = useMemo(() => {
    if (!lineOverrideSelectedRow) return false;
    const selectedBinOption = binOptions.find((bin) => bin.key === lineOverrideForm.binKey) || null;
    const normalized = (value) => String(value || "").trim().toLowerCase();
    return (
      normalized(lineOverrideSelectedRow?.boxRef) !== normalized(lineOverrideForm.boxRef)
      || normalized(lineOverrideSelectedRow?.warehouseLevel) !== normalized(selectedBinOption?.warehouseLevel)
      || normalized(lineOverrideSelectedRow?.warehouseCell) !== normalized(selectedBinOption?.warehouseCell)
      || normalized(lineOverrideSelectedRow?.binSubLocation) !== normalized(lineOverrideForm.binSubLocation)
      || normalized(lineOverrideSelectedRow?.binShelfLevel) !== normalized(lineOverrideForm.binShelfLevel)
    );
  }, [lineOverrideSelectedRow, lineOverrideForm.boxRef, lineOverrideForm.binKey, lineOverrideForm.binSubLocation, lineOverrideForm.binShelfLevel, binOptions]);

  const scopedProjectNotes = useMemo(() => {
    const sourceNotes = Array.isArray(projectOverviewNotes) ? projectOverviewNotes : [];
    return sourceNotes.filter((note) => {
      const noteOrderId = String(note?.orderId || note?.OrderId || "").trim();
      if (activeOrderId && noteOrderId && noteOrderId !== activeOrderId) return false;
      return true;
    });
  }, [projectOverviewNotes, activeOrderId]);
  const filteredSortedProjectNotes = useMemo(() => {
    const q = String(projectNotesSearch || "").trim().toLowerCase();
    return scopedProjectNotes
      .filter((note) => {
        const actor = noteActorName(note).toLowerCase();
        const msg = normalizeNoteMessage(note).toLowerCase();
        const role = String(note?.userRole || note?.role || "").toLowerCase();
        const isProduction = role.includes("production") || String(note?.scope || "").toLowerCase() === "production";
        if (projectNotesFilterProductionOnly && !isProduction) return false;
        if (projectNotesUserFilter && actor !== projectNotesUserFilter) return false;
        if (q && !(`${actor} ${msg}`.includes(q))) return false;
        return true;
      })
      .sort((a, b) => {
        const aProd = String(a?.userRole || a?.role || "").toLowerCase().includes("production") || String(a?.scope || "").toLowerCase() === "production";
        const bProd = String(b?.userRole || b?.role || "").toLowerCase().includes("production") || String(b?.scope || "").toLowerCase() === "production";
        if (aProd !== bProd) return aProd ? -1 : 1;
        return new Date(b?.createdAt || b?.at || 0).getTime() - new Date(a?.createdAt || a?.at || 0).getTime();
      });
  }, [scopedProjectNotes, projectNotesSearch, projectNotesFilterProductionOnly, projectNotesUserFilter]);
  const visibleProjectNotes = useMemo(() => filteredSortedProjectNotes.slice(0, projectNotesVisibleCount), [filteredSortedProjectNotes, projectNotesVisibleCount]);
  const projectNoteUsers = useMemo(() => [...new Set(scopedProjectNotes.map((n) => noteActorName(n)).filter(Boolean))].sort(), [scopedProjectNotes]);
  const filteredMentionUsers = useMemo(() => {
    const q = String(mentionQuery || "").trim().toLowerCase();
    if (!q) return mentionUsers;
    return mentionUsers.filter((u) =>
      String(u?.name || "").toLowerCase().includes(q) || String(u?.email || "").toLowerCase().includes(q)
    );
  }, [mentionUsers, mentionQuery]);

  const insertMention = (user) => {
    const ta = noteTextareaRef.current;
    if (!ta || !user) return;
    const { from, to } = mentionRangeRef.current || {};
    if (from == null || to == null) return;
    const label = user?.name || user?.email || `User ${user?.id}`;
    const token = `@[${label}](${user?.id})`;
    const next = `${projectNoteInput.slice(0, from)}${token}${projectNoteInput.slice(to)}`;
    setProjectNoteInput(next);
    const cursor = from + token.length;
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(cursor, cursor);
    });
    setMentionOpen(false);
    setMentionQuery("");
    setMentionIndex(0);
    mentionRangeRef.current = { from: null, to: null };
  };

  const postProjectOverviewNote = async () => {
    const trimmed = String(projectNoteInput || "").trim();
    if (!trimmed || !activeOrderId) return;
    try {
      setProjectNotePostState({ saving: true, error: "", message: "" });
      await axios.post(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/chat/order-note`,
        { orderId: activeOrderId, messageText: trimmed, messageHtml: renderMentionsAsHtml(trimmed) },
        { headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` } }
      );
      setProjectOverviewNotes((prev) => [{
        id: `local-${Date.now()}`,
        orderId: activeOrderId,
        messageText: trimmed,
        createdAt: new Date().toISOString(),
        scope: "production",
        userRole: "production-rep",
        by: "You",
      }, ...(Array.isArray(prev) ? prev : [])]);
      setProjectNoteInput("");
      setProjectNotePostState({ saving: false, error: "", message: "Note added." });
    } catch (error) {
      setProjectNotePostState({ saving: false, error: error?.response?.data?.message || "Failed to add note.", message: "" });
    }
  };

  useEffect(() => {
    if (!receiptDialog.open) return;
    const po = projectPoRows.find((row) => Number(row?.id) === Number(targetPoId));
    if (!po) return;
    const lineItems = parsePoLineItems(po);
    const initialLine = lineItems[0] || null;
    setForm((prev) => ({
      ...prev,
      poLineKey: initialLine?.lineKey || "",
      poLineLabel: initialLine?.lineLabel || "",
      goodsName: initialLine?.lineLabel || prev.goodsName,
      goodsDescription: initialLine?.description || prev.goodsDescription,
      expectedQuantity: initialLine?.orderedQuantity ?? prev.expectedQuantity,
      unit: initialLine?.unit || prev.unit,
    }));
    setLineReceiveDrafts({});
    setIsCorrectionMode(false);
    setOptimisticReceivedLockPoId(null);
    setLineReceiveOverrides({});
    setLineOverrideDialog({ open: false, lineKey: "" });
    loadReceiptsForPo(Number(po.id));
    loadReceiptEventsForPo(Number(po.id));
  }, [targetPoId, receiptDialog.open, selectedWarehouseKey, historyWarehouseKey, showReceivedHistory]);

  useEffect(() => {
    if (!receiptDialog.open) return;
    if (!selectedLineForReceiveTable) return;
    const nextQuantity = computeLineSaveQuantity(selectedLineForReceiveTable);
    if (!Number.isFinite(nextQuantity)) return;
    const currentQuantity = Number(form.quantity);
    if (Number.isFinite(currentQuantity) && currentQuantity === nextQuantity) return;
    setForm((prev) => ({ ...prev, quantity: nextQuantity }));
  }, [receiptDialog.open, selectedLineForReceiveTable, isCorrectionMode, form.quantity]);

  useEffect(() => {
    if (!lineOverrideDialog.open || !lineOverrideExistingRows.length) return;
    const selected = lineOverrideExistingRows.find((receipt) => Number(receipt?.id) === Number(lineOverrideRowSelectionId))
      || lineOverrideExistingRows[0];
    const matchedBin = binOptions.find(
      (bin) =>
        String(bin?.warehouseLevel || "") === String(selected?.warehouseLevel || "")
        && String(bin?.warehouseCell || "") === String(selected?.warehouseCell || "")
    );
    setLineOverrideRowSelectionId(Number(selected?.id) || null);
    setLineOverrideRowQuantity(String(Number(selected?.quantityExact ?? selected?.quantity) || 0));
    setLineOverrideForm((prev) => ({
      ...prev,
      refMode: String(selected?.refMode || "").trim().toLowerCase() === "untracked" ? "untracked" : prev.refMode,
      boxRef: String(selected?.boxRef || prev.boxRef || ""),
      containerType: String(selected?.containerType || prev.containerType || "box"),
      containerCount: Number(selected?.containerCount) || prev.containerCount || 1,
      binKey: matchedBin?.key || prev.binKey || "",
      binSubLocation: selected?.binSubLocation || "",
      binShelfLevel: selected?.binShelfLevel || "",
    }));
  }, [lineOverrideDialog.open, lineOverrideExistingRows, lineOverrideRowSelectionId, binOptions]);

  useEffect(() => {
    if (!receiptDialog.open) return;
    const poNotes = activePo?.customerNotes || selectedPo?.customerNotes || [];
    setProjectOverviewNotes(Array.isArray(poNotes) ? poNotes : []);
    setProjectNotesVisibleCount(20);
    setProjectNotePostState({ saving: false, error: "", message: "" });
  }, [receiptDialog.open, activePo, selectedPo]);

  return (
    <div className="instance-dashboard">
      <InstanceLeftSidebar />
      <div className="rightSide">
        <Box sx={{ p: 3, width: "100%" }}>
          <Typography variant="h4" sx={{ mb: 1 }}>Receiving</Typography>
          <Typography variant="body2" sx={{ opacity: 0.75, mb: 2 }}>
            Manage labels and receive eligible purchase orders into mapped warehouse bins.
          </Typography>

          <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
            <TextField size="small" label="Search PO / project / supplier" value={query} onChange={(e) => setQuery(e.target.value)} sx={{ minWidth: 320 }} />
            <FormControlLabel
              control={
                <Checkbox
                  checked={showReceivedHistory}
                  onChange={(e) => setShowReceivedHistory(e.target.checked)}
                />
              }
              label="Show Received history"
            />
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="shipping-warehouse-select">Warehouse</InputLabel>
              <Select
                labelId="shipping-warehouse-select"
                label="Warehouse"
                value={visibleWarehouseKey}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  if (showReceivedHistory) {
                    setHistoryWarehouseKey(nextValue);
                    return;
                  }
                  setSelectedWarehouseKey(nextValue);
                }}
              >
                {showReceivedHistory ? <MenuItem value={HISTORY_ALL_WAREHOUSES_KEY}>All Warehouses</MenuItem> : null}
                {warehouses.map((warehouse) => <MenuItem key={warehouse.warehouseKey} value={warehouse.warehouseKey}>{warehouse.name}</MenuItem>)}
                {!warehouses.length && !showReceivedHistory && <MenuItem value="main">Main Warehouse</MenuItem>}
              </Select>
            </FormControl>
            <Button variant="outlined" onClick={() => loadRows({ history: showReceivedHistory })}>Refresh</Button>
            {!showReceivedHistory ? (
              <Button variant="outlined" onClick={() => loadWarehouseLayout(selectedWarehouseKey)} startIcon={<WarehouseIcon fontSize="small" />}>Reload Warehouse Bins</Button>
            ) : null}
          </Box>

          {loading ? <CircularProgress /> : (
            <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>PO #</TableCell>
                      <TableCell>Project #</TableCell>
                      <TableCell>Supplier</TableCell>
                      {showReceivedHistory ? <TableCell>Received</TableCell> : null}
                      {showReceivedHistory ? <TableCell>Last Received</TableCell> : null}
                      {showReceivedHistory ? <TableCell>Warehouse</TableCell> : null}
                      <TableCell>Status</TableCell>
                      <TableCell>Shipping Method</TableCell>
                      <TableCell>{showReceivedHistory ? "Tracked Boxes" : "Boxes"}</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                  {filteredRows.map((row) => {
                    const supplierInfo = parseSupplierInfo(row?.supplierInfo);
                    const shippingMethodLabel = resolvePurchaseOrderShippingMethodLabel({
                      purchaseOrder: row,
                      supplierInfo,
                      options: shippingMethodOptions,
                    });
                    const statusText = resolvePoStatusText(row);
                    const statusDisplayText = poLabel(statusText);
                    const normalizedStatusText = String(normalizePoStatus(statusText) || "").toLowerCase();
                    const shipped = normalizedStatusText === "shipped";
                    const receiptHistory = row?.receiptHistory || null;
                    const boxes = showReceivedHistory
                      ? countReceiptHistoryTrackedBoxes(receiptHistory)
                      : parseTrackingCount(row?.trackingNumbers);
                    return (
                      <TableRow key={row?.id} hover>
                        <TableCell>{row?.poNumber || "—"}</TableCell>
                        <TableCell>{row?.orderId || "—"}</TableCell>
                        <TableCell>{supplierInfo?.supplierName || "—"}</TableCell>
                        {showReceivedHistory ? (
                          <TableCell>{receiptHistory ? formatReceivedQuantitySummary(receiptHistory) : "—"}</TableCell>
                        ) : null}
                        {showReceivedHistory ? (
                          <TableCell>{formatEventTimestamp(receiptHistory?.lastReceivedAt)}</TableCell>
                        ) : null}
                        {showReceivedHistory ? (
                          <TableCell>{receiptHistory?.warehouseDisplayName || "—"}</TableCell>
                        ) : null}
                        <TableCell><Tooltip title={poTooltip(statusText) || statusDisplayText}><Chip size="small" label={statusDisplayText} color={shipped ? "success" : "default"} variant={shipped ? "filled" : "outlined"} /></Tooltip></TableCell>
                        <TableCell>{shippingMethodLabel}</TableCell>
                        <TableCell>{boxes || "—"}</TableCell>
                        <TableCell align="right">
                          <Button size="small" startIcon={<OpenInNewIcon fontSize="small" />} onClick={() => window.open(`/generated-purchase-order/${row?.id}`, "_blank", "noopener,noreferrer")}>Open PO</Button>
                          <Button size="small" startIcon={<PrintIcon fontSize="small" />} onClick={() => window.open(`/instance/${instanceUniqueId}/purchase-orders/${row?.id}/print/shipping-label`, "_blank", "noopener,noreferrer")}>Print Label</Button>
                          <Button size="small" startIcon={<InventoryIcon fontSize="small" />} onClick={() => openReceiveDialog(row)}>{showReceivedHistory ? "View History" : "Receive"}</Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {!filteredRows.length && (
                    <TableRow>
                      <TableCell colSpan={showReceivedHistory ? 10 : 7} align="center">
                        {showReceivedHistory
                          ? historyWarehouseKey === HISTORY_ALL_WAREHOUSES_KEY
                            ? "No received purchase-order history found for this instance."
                            : "No received purchase-order history found for this warehouse."
                          : Array.isArray(receivingEligiblePoStatuses) && receivingEligiblePoStatuses.length === 0
                          ? "No purchase-order statuses are currently enabled for receiving. Update Shipping Settings in Company Settings."
                          : "No shipping labels found."}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </div>

      <Dialog open={receiptDialog.open} onClose={closeDialog} maxWidth="lg" fullWidth>
        <DialogTitle>Receive Goods Into Warehouse</DialogTitle>
        <DialogContent>
          {selectedPo ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "flex-start", sm: "center" }}>
                <Avatar
                  src={goodsImageUrl || undefined}
                  variant="circular"
                  alt={form.goodsName || selectedPo?.poNumber || "Goods image"}
                  sx={{ width: 60, height: 60, bgcolor: "#ecf1f8", border: "1px solid #d6e0ef" }}
                >
                  {goodsImageUrl ? "" : "PO"}
                </Avatar>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.75 }}>PO #{activePo?.poNumber || activePo?.id} · Project #{selectedPo?.orderId || "—"}</Typography>
                  <Typography variant="caption" sx={{ display: "block", opacity: 0.72 }}>
                    {expectedTrackedContainers ? `${receivedBoxCount}/${expectedTrackedContainers} tracked containers received` : `${receivedBoxCount} container refs logged (no tracking uploaded yet)`}
                    {receivedUnits ? ` · ${receivedUnits} total units received` : ""}
                  </Typography>
                </Box>
              </Stack>

              <Paper variant="outlined" sx={{ p: 1.25 }}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ xs: "flex-start", md: "center" }}>
                  <Chip size="small" label={`Project POs: ${projectPoRows.length || 0}`} variant="outlined" />
                  <Chip
                    size="small"
                    color={expectedTrackedContainers ? "info" : "warning"}
                    label={expectedTrackedContainers
                      ? `Tracked refs: ${projectTrackedRefsReceived}/${projectExpectedTrackedContainers}`
                      : "No tracked refs uploaded"}
                    variant="outlined"
                  />
                  {!isReceivableStatus && (
                    <Chip
                      size="small"
                      color="warning"
                      label={`PO status is ${poLabel(activePo?.supplierStatus)} (receiving disabled)`}
                      variant="outlined"
                    />
                  )}
                  {isReadOnly && (
                    <Chip
                      size="small"
                      color="default"
                      label="Received: read-only"
                      variant="outlined"
                    />
                  )}
                  {isCorrectionMode && (
                    <Chip
                      size="small"
                      color="info"
                      label="Correction Mode (Unreceive)"
                      variant="outlined"
                    />
                  )}
                  <Typography variant="caption" sx={{ opacity: 0.75, ml: { md: "auto" } }}>
                    Save recalculates PO status to {poLabel("Partially Received")} / {poLabel("Received")}.
                  </Typography>
                </Stack>
              </Paper>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <Stack spacing={0.8} sx={{ flex: 1 }}>
                  <FormControl size="small" fullWidth>
                    <InputLabel id="receive-boxref-label">Container / Tracking Ref</InputLabel>
                    <Select
                      labelId="receive-boxref-label"
                      value={boxRefMode === "untracked" ? UNTRACKED_OPTION : (form.boxRef || "")}
                      label="Container / Tracking Ref"
                      disabled={isReadOnly}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === UNTRACKED_OPTION) {
                          setBoxRefMode("untracked");
                          setForm((prev) => ({ ...prev, boxRef: "" }));
                          return;
                        }

                        setBoxRefMode("tracked");
                        const existing = poReceipts.find(
                          (receipt) => normalizeTrackingRef(receipt?.boxRef) === normalizeTrackingRef(value)
                        );
                        if (existing) applyExistingReceiptToForm(existing, value);
                        else setForm((prev) => ({ ...prev, boxRef: value }));
                      }}
                    >
                      {trackedRefOptions.map((boxRef) => <MenuItem key={boxRef} value={boxRef}>{boxRef}</MenuItem>)}
                      <MenuItem value={UNTRACKED_OPTION}>Untracked / Other reference</MenuItem>
                    </Select>
                  </FormControl>
                  {boxRefMode === "untracked" && (
                    <TextField
                      size="small"
                      fullWidth
                      label="Untracked Receiving Ref"
                      disabled={isReadOnly}
                      placeholder="e.g. 5 boxes / Pallet A / Handwritten slip #"
                      value={form.boxRef}
                      onChange={(e) => {
                        const value = e.target.value;
                        setForm((prev) => ({ ...prev, boxRef: value }));
                        const nextRef = value.trim();
                        const existing = poReceipts.find(
                          (receipt) => normalizeTrackingRef(receipt?.boxRef) === normalizeTrackingRef(nextRef)
                        );
                        if (existing) applyExistingReceiptToForm(existing, nextRef);
                      }}
                    />
                  )}
                </Stack>

                <Stack spacing={0.8} sx={{ flex: 1 }}>
                  <FormControl size="small" fullWidth>
                    <InputLabel id="container-type-label">Container Type</InputLabel>
                    <Select
                      labelId="container-type-label"
                      value={form.containerType}
                      label="Container Type"
                      disabled={isReadOnly}
                      onChange={(e) => setForm((prev) => ({ ...prev, containerType: e.target.value }))}
                    >
                      <MenuItem value="box">Box</MenuItem>
                      <MenuItem value="pallet">Pallet</MenuItem>
                      <MenuItem value="carton">Carton</MenuItem>
                      <MenuItem value="crate">Crate</MenuItem>
                      <MenuItem value="roll">Roll</MenuItem>
                      <MenuItem value="bundle">Bundle</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    type="number"
                    fullWidth
                    label="Container Count"
                    disabled={isReadOnly}
                    value={form.containerCount}
                    onChange={(e) => setForm((prev) => ({ ...prev, containerCount: e.target.value }))}
                    inputProps={{ min: 1 }}
                  />
                </Stack>

                <FormControl size="small" fullWidth>
                  <InputLabel id="receive-bin-label">Warehouse Bin</InputLabel>
                  <Select
                    labelId="receive-bin-label"
                    value={form.binKey}
                    label="Warehouse Bin"
                    disabled={isReadOnly}
                    onChange={(e) => {
                      const nextKey = e.target.value;
                      const nextBin = binOptions.find((bin) => bin.key === nextKey);
                      setForm((prev) => ({ ...prev, binKey: nextKey }));
                      loadSlotSuggestionsForBin(nextBin || null);
                    }}
                  >
                    <MenuItem value=""><em>Unassigned</em></MenuItem>
                    {binOptions.map((bin) => <MenuItem key={bin.key} value={bin.key}>{bin.levelName} · {bin.label} ({bin.warehouseCell})</MenuItem>)}
                  </Select>
                </FormControl>

                <Button variant="outlined" onClick={handleOpenBinPicker} sx={{ minWidth: 170 }} disabled={isReadOnly}>
                  Pick Bin on Map
                </Button>
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="target-po-select-label">Target PO</InputLabel>
                  <Select
                    labelId="target-po-select-label"
                    label="Target PO"
                    value={targetPoId || ""}
                    disabled={isReadOnly}
                    onChange={(e) => setTargetPoId(Number(e.target.value) || null)}
                  >
                    {projectPoRows.map((poRow) => (
                      <MenuItem key={`target-po-${poRow.id}`} value={poRow.id}>
                        PO #{poRow?.poNumber || poRow?.id} · {poLabel(poRow?.supplierStatus)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<OpenInNewIcon fontSize="small" />}
                  disabled={!targetPoId}
                  onClick={() => {
                    if (!targetPoId) return;
                    window.open(`/generated-purchase-order/${targetPoId}`, "_blank", "noopener,noreferrer");
                  }}
                  sx={{ minWidth: 132 }}
                >
                  View PO
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<OpenInNewIcon fontSize="small" />}
                  disabled={!activePo?.orderId}
                  onClick={() => {
                    if (!activePo?.orderId) return;
                    window.open(
                      `/instance/${instanceUniqueId}/sales/orders/${activePo.orderId}/overview`,
                      "_blank",
                      "noopener,noreferrer"
                    );
                  }}
                  sx={{ minWidth: 150 }}
                >
                  View Project
                </Button>
              </Stack>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>PO Line / Product</TableCell>
                      <TableCell align="right">Expected</TableCell>
                      <TableCell align="right">Received</TableCell>
                      <TableCell align="right">Remaining</TableCell>
                      <TableCell align="center">Storage</TableCell>
                      <TableCell align="center">Fully received</TableCell>
                      <TableCell align="right">{isCorrectionMode ? "Target received" : "Receive now"}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!lineItemsForReceiveTable.length && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          No PO line items available for this PO.
                        </TableCell>
                      </TableRow>
                    )}
                    {lineItemsForReceiveTable.map((line) => {
                      const draftNum = Number(line.rowInputValue);
                      const remaining = Number(line.remainingQuantity);
                      const fullyChecked = Number.isFinite(remaining) && Number.isFinite(draftNum)
                        ? (isCorrectionMode
                          ? draftNum >= Number(line.orderedQuantity)
                          : draftNum >= Math.max(remaining, 0))
                        : false;
                      const isSelected = normalizeLineKey(form.poLineKey) === normalizeLineKey(line.lineKey);
                      return (
                        <TableRow key={`receive-line-${line.lineKey}`} selected={isSelected}>
                          <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Avatar
                                src={line.thumbnail || undefined}
                                variant="rounded"
                                alt={line.lineLabel}
                                sx={{ width: 32, height: 32, bgcolor: "#eef2f8", border: "1px solid #d6e0ef" }}
                              >
                                {line.thumbnail ? "" : "PO"}
                              </Avatar>
                              <Button
                                size="small"
                                variant={isSelected ? "contained" : "text"}
                                onClick={() => applyLineReceiveSelection(line, line.rowInputValue)}
                                disabled={isReadOnly}
                              >
                                {line.lineLabel}
                              </Button>
                            </Stack>
                          </TableCell>
                          <TableCell align="right">{Number.isFinite(line.orderedQuantity) ? line.orderedQuantity : "—"}</TableCell>
                          <TableCell align="right">{line.receivedQuantity}</TableCell>
                          <TableCell align="right">{line.remainingQuantity == null ? "—" : line.remainingQuantity}</TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                              {lineHasOverride(line.lineKey) && <Chip size="small" color="info" label="Unique bin location" variant="outlined" />}
                              <IconButton
                                size="small"
                                onClick={() => openLineOverrideDialog(line)}
                                disabled={isReadOnly}
                              >
                                <TuneIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </TableCell>
                          <TableCell align="center">
                            <Checkbox
                              checked={fullyChecked}
                              onChange={(e) => handleLineFullyReceivedToggle(line, e.target.checked)}
                              disabled={isReadOnly}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ minWidth: 140 }}>
                            <TextField
                              size="small"
                              type="number"
                              value={line.rowInputValue}
                              onChange={(e) => handleLineReceiveQtyChange(line, e.target.value)}
                              disabled={isReadOnly}
                              inputProps={{
                                min: 0,
                                step: "1",
                                max: isCorrectionMode ? undefined : (line.maxReceivableNow == null ? undefined : line.maxReceivableNow),
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {selectedLineItem && (
                <Typography variant="caption" sx={{ opacity: 0.75 }}>
                  {selectedLineItem.lineLabel}: received {receivedLineUnits}
                  {Number.isFinite(selectedLineItem.orderedQuantity) ? ` / ordered ${selectedLineItem.orderedQuantity}` : ""}
                  {lineRemainingQty == null ? "" : ` · remaining ${lineRemainingQty}`}
                  {selectedLineForReceiveTable?.lineVarianceQuantity == null
                    ? ""
                    : ` · line variance ${selectedLineForReceiveTable.lineVarianceQuantity > 0 ? "+" : ""}${selectedLineForReceiveTable.lineVarianceQuantity}`}
                </Typography>
              )}

              {poReceiptEvents.length > 0 && (
                <Accordion disableGutters elevation={0} sx={{ border: "1px solid #dbe3ee", borderRadius: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 42 }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Shipping Log
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.72 }}>
                        Recent line-level receive updates for this PO.
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={1}>
                      {receiptEventsLoading && <CircularProgress size={18} />}
                      {receiptEventsError && <Alert severity="error">{receiptEventsError}</Alert>}
                      {poReceiptEvents.map((event, index) => {
                        const actor = String(event?.userName || event?.userEmail || event?.userId || "Unknown");
                        const lineLabel = String(event?.poLineLabel || event?.lineLabel || event?.poLineKey || "Line");
                        const mode = String(event?.quantityMode || event?.mode || "add");
                        const delta = Number(event?.quantityDelta);
                        const resulting = Number(event?.resultingQuantity ?? event?.netReceivedQuantity);
                        const trackingRef = String(event?.boxRef || event?.trackingRef || "").trim();
                        const location = [event?.binLabel, event?.binSubLocation, event?.binShelfLevel].filter(Boolean).join(" · ");
                        return (
                          <Box key={`${event?.id || event?.eventId || index}`} sx={{ border: "1px solid #ecf1f8", borderRadius: 1, px: 1.25, py: 0.75 }}>
                            <Typography variant="caption" sx={{ display: "block", opacity: 0.78 }}>
                              {formatEventTimestamp(event?.createdAt || event?.timestamp)} · {actor}
                            </Typography>
                            <Typography variant="body2">
                              {lineLabel}
                              {trackingRef ? ` · Ref ${trackingRef}` : ""}
                              {Number.isFinite(delta) ? ` · Δ ${delta > 0 ? "+" : ""}${delta}` : ""}
                              {Number.isFinite(resulting) ? ` · Net ${resulting}` : ""}
                              {mode ? ` · ${mode}` : ""}
                              {location ? ` · ${location}` : ""}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              )}

              <Box sx={{ border: "1px solid #dbe3ee", borderRadius: 1 }}>
                <Tabs value={receiveNotesTab} onChange={(_, value) => setReceiveNotesTab(value)} sx={{ px: 1 }}>
                  <Tab value="additional" label="Additional notes" />
                  <Tab value="overview" label="Project overview" />
                </Tabs>
                <Box sx={{ p: 1.5, pt: 0.75 }}>
                  {receiveNotesTab === "additional" ? (
                    <Accordion disableGutters elevation={0} sx={{ border: "1px solid #dbe3ee", borderRadius: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 42 }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Additional order notes
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.72 }}>
                            Optional details for internal receiving records.
                            {varianceQty == null
                              ? ""
                              : ` Current variance on selected line: ${varianceQty > 0 ? "+" : ""}${varianceQty}`}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Stack spacing={1.25}>
                          <TextField size="small" fullWidth label="Variance Reason" placeholder="Required when short/over if applicable" value={form.varianceReason} disabled={isReadOnly} onChange={(e) => setForm((prev) => ({ ...prev, varianceReason: e.target.value }))} />
                          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                            <TextField size="small" fullWidth label="Bin Sub-location" placeholder="e.g. Pallet A / Slot 3" value={form.binSubLocation} disabled={isReadOnly} onChange={(e) => setForm((prev) => ({ ...prev, binSubLocation: e.target.value }))} helperText={slotSuggestions.subLocation.length ? `Suggested: ${slotSuggestions.subLocation.join(", ")}` : ""} />
                            <TextField size="small" fullWidth label="Shelf/Level" placeholder="e.g. Level 2" value={form.binShelfLevel} disabled={isReadOnly} onChange={(e) => setForm((prev) => ({ ...prev, binShelfLevel: e.target.value }))} helperText={slotSuggestions.shelfLevel.length ? `Suggested: ${slotSuggestions.shelfLevel.join(", ")}` : ""} />
                          </Stack>
                          <TextField size="small" fullWidth multiline minRows={2} label="Goods Description" value={form.goodsDescription} disabled={isReadOnly} onChange={(e) => setForm((prev) => ({ ...prev, goodsDescription: e.target.value }))} />
                          <TextField size="small" fullWidth multiline minRows={2} label="Receiving Notes" value={form.notes} disabled={isReadOnly} onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))} />
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  ) : (
                    <Stack spacing={1.25}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>Project overview</Typography>
                      <Typography variant="caption" sx={{ opacity: 0.75 }}>Production notes and project context for this order.</Typography>
                      <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                        <FormControlLabel control={<Checkbox checked={projectNotesFilterProductionOnly} onChange={(e) => setProjectNotesFilterProductionOnly(e.target.checked)} />} label="Production only" />
                        <FormControl size="small" sx={{ minWidth: 220 }}>
                          <InputLabel>Filter by user</InputLabel>
                          <Select value={projectNotesUserFilter} label="Filter by user" onChange={(e) => setProjectNotesUserFilter(e.target.value)}>
                            <MenuItem value="">All users</MenuItem>
                            {projectNoteUsers.map((userName) => <MenuItem key={userName} value={userName}>{userName}</MenuItem>)}
                          </Select>
                        </FormControl>
                        <TextField size="small" label="Search notes" value={projectNotesSearch} onChange={(e) => setProjectNotesSearch(e.target.value)} sx={{ minWidth: 220 }} />
                      </Stack>
                      {!activeOrderId && <Alert severity="warning">Missing project/order id.</Alert>}
                      <Box sx={{ position: "relative" }}>
                        <TextField
                          inputRef={noteTextareaRef}
                          size="small"
                          fullWidth
                          multiline
                          minRows={3}
                          label="Production note"
                          placeholder="Add a production note..."
                          value={projectNoteInput}
                          onChange={(e) => setProjectNoteInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (!mentionOpen) return;
                            if (e.key === "Escape") {
                              setMentionOpen(false);
                              return;
                            }
                            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                              e.preventDefault();
                              const max = filteredMentionUsers.length;
                              if (!max) return;
                              setMentionIndex((i) => (e.key === "ArrowDown" ? (i + 1) % max : (i - 1 + max) % max));
                            }
                            if (e.key === "Enter" && filteredMentionUsers.length) {
                              e.preventDefault();
                              insertMention(filteredMentionUsers[mentionIndex]);
                            }
                          }}
                          onKeyUp={(e) => {
                            const ta = noteTextareaRef.current || e.currentTarget;
                            const upto = String(ta?.value || "").slice(0, ta?.selectionStart || 0);
                            const m = upto.match(/@([a-z0-9 _.\-]{0,30})$/i);
                            if (m) {
                              const sel = ta?.selectionStart || 0;
                              mentionRangeRef.current = { from: sel - m[0].length, to: sel };
                              setMentionQuery(m[1] || "");
                              setMentionOpen(true);
                              setMentionIndex(0);
                            } else {
                              mentionRangeRef.current = { from: null, to: null };
                              setMentionOpen(false);
                              setMentionQuery("");
                              setMentionIndex(0);
                            }
                          }}
                        />
                        {mentionOpen && (
                          <Box sx={{ position: "absolute", zIndex: 20, left: 8, right: 8, top: "100%", mt: 0.5, maxHeight: 220, overflowY: "auto", bgcolor: "white", border: "1px solid #e0e0e0", borderRadius: 1, boxShadow: "0 6px 18px rgba(0,0,0,0.14)" }}>
                            {!filteredMentionUsers.length ? (
                              <Box sx={{ p: 1, color: "text.secondary", fontSize: 13 }}>No users</Box>
                            ) : filteredMentionUsers.map((u, i) => (
                              <Box key={u.id} onMouseDown={(evt) => { evt.preventDefault(); insertMention(u); }} sx={{ px: 1, py: 0.8, cursor: "pointer", bgcolor: i === mentionIndex ? "rgba(0,0,0,0.05)" : "transparent", "&:hover": { bgcolor: "rgba(0,0,0,0.05)" } }}>
                                <Typography variant="body2">{u.name}</Typography>
                                <Typography variant="caption" sx={{ opacity: 0.72 }}>{u.email}</Typography>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                      <Box>
                        <Button variant="contained" onClick={postProjectOverviewNote} disabled={!activeOrderId || !String(projectNoteInput || "").trim() || projectNotePostState.saving}>
                          {projectNotePostState.saving ? "Adding..." : "Add note"}
                        </Button>
                      </Box>
                      {projectNotePostState.message && <Alert severity="success">{projectNotePostState.message}</Alert>}
                      {projectNotePostState.error && <Alert severity="error">{projectNotePostState.error}</Alert>}
                      <Stack spacing={1}>
                        {visibleProjectNotes.map((note, index) => (
                          <Paper key={`${note?.id || index}`} variant="outlined" sx={{ p: 1 }}>
                            <Typography variant="caption" sx={{ opacity: 0.72 }}>
                              {noteActorName(note)} · {formatEventTimestamp(note?.createdAt || note?.at)}
                            </Typography>
                            <Typography variant="body2">{note?.isDeleted ? "Comment deleted" : (normalizeNoteMessage(note) || "Comment deleted")}</Typography>
                          </Paper>
                        ))}
                        {!visibleProjectNotes.length && <Typography variant="caption" sx={{ opacity: 0.7 }}>No notes found.</Typography>}
                        {filteredSortedProjectNotes.length > visibleProjectNotes.length && (
                          <Box><Button size="small" onClick={() => setProjectNotesVisibleCount((prev) => prev + 20)}>Show more</Button></Box>
                        )}
                      </Stack>
                    </Stack>
                  )}
                </Box>
              </Box>
              <FormControlLabel
                control={<Checkbox checked={Boolean(form.postProjectComment)} onChange={(e) => setForm((prev) => ({ ...prev, postProjectComment: e.target.checked }))} disabled={isReadOnly} />}
                label="Post an automatic project PO update comment for this receipt"
              />

              {activeReceipt && (
                <Typography variant="caption" sx={{ opacity: 0.75 }}>
                  Last saved: {activeReceipt.goodsName || "Unnamed goods"}
                  {activeReceipt.binLabel ? ` → ${activeReceipt.binLabel}` : ""}
                  {activeReceipt.binSubLocation ? ` · ${activeReceipt.binSubLocation}` : ""}
                  {activeReceipt.binShelfLevel ? ` · ${activeReceipt.binShelfLevel}` : ""}
                </Typography>
              )}

              {receiptsLoading && <CircularProgress size={22} />}
              {saveState.message && <Alert severity="success">{saveState.message}</Alert>}
              {saveState.error && <Alert severity="error">{saveState.error}</Alert>}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Box sx={{ mr: "auto", display: "flex", alignItems: "center", gap: 0.75 }}>
            <Tooltip title={quantityMode === "add" ? "Add to existing quantity for this line/container." : "Replace existing quantity (for corrections)."}>
              <IconButton
                size="small"
                color={quantityMode === "add" ? "primary" : "default"}
                onClick={() => setQuantityMode((prev) => (prev === "add" ? "set" : "add"))}
                disabled={isReadOnly}
              >
                {quantityMode === "add" ? <AddCircleOutlineIcon fontSize="small" /> : <EditOutlinedIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              {quantityMode === "add" ? "Mode: Add" : "Mode: Replace"}
            </Typography>
          </Box>
          {isReceivedStatus && !isCorrectionMode ? (
            <Button
              variant="outlined"
              onClick={() => setIsCorrectionMode(true)}
            >
              Unreceive
            </Button>
          ) : null}
          <Button onClick={closeDialog}>Close</Button>
          <Button variant="contained" onClick={handleSaveReceipt} disabled={saveState.saving || isReadOnly}>{saveState.saving ? "Saving..." : "Save Receipt"}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={binPickerOpen} onClose={() => setBinPickerOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Select Warehouse Bin (View Only)</DialogTitle>
        <DialogContent>
          {warehouseLayoutLoading ? (
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ py: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">Loading warehouse layout...</Typography>
            </Stack>
          ) : warehouseLayoutError ? (
            <Alert
              severity="error"
              action={<Button color="inherit" size="small" onClick={() => loadWarehouseLayout(selectedWarehouseKey)}>Retry</Button>}
            >
              {warehouseLayoutError}
            </Alert>
          ) : !warehouseLayout ? (
            <Alert severity="info">No warehouse layout found for this warehouse yet.</Alert>
          ) : warehouseLayoutKey !== selectedWarehouseKey ? (
            <Alert
              severity="info"
              action={<Button color="inherit" size="small" onClick={() => loadWarehouseLayout(selectedWarehouseKey)}>Retry</Button>}
            >
              Warehouse layout is not loaded yet.
            </Alert>
          ) : !binOptions.length ? (
            <Alert severity="info">This warehouse layout has no selectable bins yet.</Alert>
          ) : (
            <Stack spacing={1.5}>
              <WarehouseBinPickerMap
                layout={warehouseLayout}
                activeWarehouseKey={selectedWarehouseKey}
                selectedBinKey={form.binKey}
                disabled={isReadOnly}
                showOnlyBinsSelectable
                onSelectBin={handlePickBinFromMap}
              />
              <Typography variant="caption" sx={{ opacity: 0.75 }}>Tip: click any highlighted bin block to auto-fill the Warehouse Bin select.</Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBinPickerOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={lineOverrideDialog.open} onClose={closeLineOverrideDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Line Storage Override</DialogTitle>
        <DialogContent>
          <Stack spacing={1.25} sx={{ mt: 1 }}>
            <Typography variant="caption" sx={{ opacity: 0.75 }}>
              Override applies only to this PO line. Use defaults to inherit top-level settings.
            </Typography>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>
                Existing receipt rows
              </Typography>
              {!lineOverrideExistingRows.length ? (
                <Typography variant="caption" sx={{ opacity: 0.72 }}>
                  No existing receipt rows for this line.
                </Typography>
              ) : (
                <Stack spacing={0.75}>
                  {lineOverrideExistingRows.map((receipt) => {
                    const isSelected = Number(receipt?.id) === Number(lineOverrideRowSelectionId);
                    return (
                      <Paper
                        key={`line-receipt-row-${receipt?.id}`}
                        variant="outlined"
                        onClick={() => setLineOverrideRowSelectionId(Number(receipt?.id) || null)}
                        sx={{ px: 1, py: 0.75, cursor: "pointer", borderColor: isSelected ? "primary.main" : "divider" }}
                      >
                        <Typography variant="caption" sx={{ display: "block", opacity: 0.82 }}>
                          Ref: {receipt?.boxRef || "—"} · Qty: {Number(receipt?.quantityExact ?? receipt?.quantity) || 0}
                        </Typography>
                        <Typography variant="caption" sx={{ display: "block", opacity: 0.72 }}>
                          {(receipt?.binLabel || "No bin")}
                          {receipt?.binSubLocation ? ` · ${receipt.binSubLocation}` : ""}
                          {receipt?.binShelfLevel ? ` · ${receipt.binShelfLevel}` : ""}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.6 }}>
                          Updated {formatEventTimestamp(receipt?.updatedAt || receipt?.receivedAt)}
                        </Typography>
                      </Paper>
                    );
                  })}
                </Stack>
              )}
            </Box>
            {Number.isFinite(Number(lineOverrideRowSelectionId)) && Number(lineOverrideRowSelectionId) > 0 && (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel id="line-row-mode-label">Receipt row mode</InputLabel>
                  <Select
                    labelId="line-row-mode-label"
                    label="Receipt row mode"
                    value={lineOverrideRowMode}
                    onChange={(e) => setLineOverrideRowMode(e.target.value === "add" ? "add" : "set")}
                  >
                    <MenuItem value="set">Set row quantity</MenuItem>
                    <MenuItem value="add">Add row delta</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  type="number"
                  label={lineOverrideRowMode === "add" ? "Receipt row delta" : "Receipt row target qty"}
                  value={lineOverrideRowQuantity}
                  onChange={(e) => setLineOverrideRowQuantity(e.target.value)}
                  inputProps={{ min: 0, step: "1" }}
                />
              </Stack>
            )}
            {lineOverrideDestinationChanged && lineOverrideRowMode === "add" && (
              <Alert severity="info">
                Destination change detected. Use <strong>Set row quantity</strong> for this move.
              </Alert>
            )}
            <FormControl size="small" fullWidth>
              <InputLabel id="line-override-ref-label">Container / Tracking Ref</InputLabel>
              <Select
                labelId="line-override-ref-label"
                value={lineOverrideForm.refMode === "untracked" ? UNTRACKED_OPTION : (lineOverrideForm.boxRef || "")}
                label="Container / Tracking Ref"
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === UNTRACKED_OPTION) {
                    setLineOverrideForm((prev) => ({ ...prev, refMode: "untracked", boxRef: "" }));
                    return;
                  }
                  setLineOverrideForm((prev) => ({ ...prev, refMode: "tracked", boxRef: value }));
                }}
              >
                {trackedRefOptions.map((boxRef) => <MenuItem key={`line-override-ref-${boxRef}`} value={boxRef}>{boxRef}</MenuItem>)}
                <MenuItem value={UNTRACKED_OPTION}>Untracked / Other reference</MenuItem>
              </Select>
            </FormControl>
            {lineOverrideForm.refMode === "untracked" && (
              <TextField
                size="small"
                label="Untracked Receiving Ref"
                value={lineOverrideForm.boxRef}
                onChange={(e) => setLineOverrideForm((prev) => ({ ...prev, boxRef: e.target.value }))}
              />
            )}
            <FormControl size="small" fullWidth>
              <InputLabel id="line-override-container-type-label">Container Type</InputLabel>
              <Select
                labelId="line-override-container-type-label"
                value={lineOverrideForm.containerType}
                label="Container Type"
                onChange={(e) => setLineOverrideForm((prev) => ({ ...prev, containerType: e.target.value }))}
              >
                <MenuItem value="box">Box</MenuItem>
                <MenuItem value="pallet">Pallet</MenuItem>
                <MenuItem value="carton">Carton</MenuItem>
                <MenuItem value="crate">Crate</MenuItem>
                <MenuItem value="roll">Roll</MenuItem>
                <MenuItem value="bundle">Bundle</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              type="number"
              label="Container Count"
              value={lineOverrideForm.containerCount}
              onChange={(e) => setLineOverrideForm((prev) => ({ ...prev, containerCount: e.target.value }))}
              inputProps={{ min: 1 }}
            />
            <FormControl size="small" fullWidth>
              <InputLabel id="line-override-bin-label">Warehouse Bin</InputLabel>
              <Select
                labelId="line-override-bin-label"
                value={lineOverrideForm.binKey}
                label="Warehouse Bin"
                onChange={(e) => setLineOverrideForm((prev) => ({ ...prev, binKey: e.target.value }))}
              >
                <MenuItem value=""><em>Unassigned</em></MenuItem>
                {binOptions.map((bin) => <MenuItem key={`line-override-${bin.key}`} value={bin.key}>{bin.levelName} · {bin.label} ({bin.warehouseCell})</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Bin Sub-location"
              value={lineOverrideForm.binSubLocation}
              onChange={(e) => setLineOverrideForm((prev) => ({ ...prev, binSubLocation: e.target.value }))}
            />
            <TextField
              size="small"
              label="Shelf/Level"
              value={lineOverrideForm.binShelfLevel}
              onChange={(e) => setLineOverrideForm((prev) => ({ ...prev, binShelfLevel: e.target.value }))}
            />
            {activeLineOverride?.enabled && (
              <Chip size="small" color="info" label="Currently using line-specific override" variant="outlined" />
            )}
            {lineOverrideSaveState.message && <Alert severity="success">{lineOverrideSaveState.message}</Alert>}
            {lineOverrideSaveState.error && <Alert severity="error">{lineOverrideSaveState.error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={clearLineOverride}>Use defaults</Button>
          <Button onClick={closeLineOverrideDialog}>Cancel</Button>
          <Button variant="contained" onClick={saveLineOverride} disabled={lineOverrideSaveState.saving}>
            {lineOverrideSaveState.saving
              ? "Saving..."
              : (Number.isFinite(Number(lineOverrideRowSelectionId)) && Number(lineOverrideRowSelectionId) > 0
                ? "Save Receipt Row"
                : "Save Line Override")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
